import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, timer } from 'rxjs';
import { map, catchError, filter, finalize, take, tap } from 'rxjs/operators';
import { BaseService } from 'src/app/common/base/base.service';
import {
  ChatMessage, ChatLink, ParsedIntent, IntentType,
  FORM_SERIAL_PATTERN, FORM_TYPE_WORDS, STATUS_WORDS,
  WorkflowPendingSignature, WorkflowAssistantInterpretResponse,
} from './workflow-assistant.models';
import { RsswaService } from '../rsswa/rsswa.service';
import { FormStatusEnum, FormType, ResourceType, ReviewCycleStatus, Roles } from 'src/app/common/enumerations/enumerations';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { PendingApprovalsV2Service } from '../pending-approvals-v2/pending-approvals-v2.service';
import { DocumentsState } from 'src/app/services/documents/store/documents.state';
import { DocumentsService } from 'src/app/services/documents/documents.service';
import { Document, DocumentCategory } from 'src/app/services/documents/documents';
import { RolePrivilegeState } from 'src/app/services/role-privilege/store/role-privilege/role-privilege.state';
import { PrivilegeState } from 'src/app/services/role-privilege/store/privilege/privilege.state';
import { BeamlineStatusState } from '../home/beamline-status/store/bl-status.state';
import { AcceleratorStatusState } from '../home/accelerator-status/store/accelerator-status.state';
import { BeamlineStatus } from '../home/beamline-status/beamline-status';
import { AcceleratorStatus } from '../home/accelerator-status/accelerator.service';
import { Resource, IResource } from '../catalogs/beamline-catalog/resource/resources';
import { WFSectionLocalResource, WfSignatureResource } from '../workflow/workflow';
import { ProcedureReport } from '../procedure/models/procedure-report.model';
import { ProcedureReportService } from '../procedure/services/procedure-report.service';
import { Schedule } from '../schedules/models/schedule';
import { ScheduleStatusEnum, ScheduleStatusTimeEnum } from '../schedules/models/enums';
import { ScheduleService } from '../schedules/services/schedule.service';
import { SchedulesState } from '../schedules/store/schedules/schedule.state';
import { Review, ReviewCycle } from '../reviews/reviews.models';
import { ReviewsService } from '../reviews/reviews.service';
import { ReviewCyclesState } from '../reviews/store/review-cycles/review-cycles.state';
import { ReviewsState } from '../reviews/store/reviews/reviews.state';

@Injectable({ providedIn: 'root' })
export class WorkflowAssistantService extends BaseService<any, any> {

  private readonly cborgUnavailableSubject = new BehaviorSubject<boolean>(false);
  private cborgAvailabilityChecked = false;
  private cborgAvailabilityCheckInProgress = false;
  readonly cborgUnavailable$ = this.cborgUnavailableSubject.asObservable();

  constructor(
    protected override injector: Injector,
    private rsswaService: RsswaService,
    private pendingService: PendingApprovalsV2Service,
    private documentsService: DocumentsService,
    private procedureReportService: ProcedureReportService,
    private scheduleService: ScheduleService,
    private reviewsService: ReviewsService,
  ) {
    super(injector, '');
  }

  // ------------------------------------------------------------
  // Intent Parser
  // ------------------------------------------------------------

  /**
   * Probes CBorg with a fixed question containing no ASCC records when the assistant first opens.
   * Successful checks are cached for the session; unavailable checks may be retried when the panel reopens.
   */
  checkCBorgAvailability(): void {
    if (this.cborgAvailabilityCheckInProgress || (this.cborgAvailabilityChecked && !this.cborgUnavailableSubject.value)) return;

    this.cborgAvailabilityCheckInProgress = true;
    this.http.post<WorkflowAssistantInterpretResponse>(`${this.BASE_URL}/WorkflowAssistant/Interpret`, { question: 'What can I do in ASCC?' }).pipe(
      finalize(() => this.cborgAvailabilityCheckInProgress = false),
    ).subscribe({
      next: response => this.recordCBorgAvailability(response?.available),
      error: () => this.recordCBorgAvailability(false),
    });
  }

  /**
   * Classifies a question using the established local ASCC rules.
   * @param text Question entered by the current user.
   * @returns A structured intent that can be handled without AI.
   */
  parseIntent(text: string): ParsedIntent {
    const lower = this.normalizeQuestionText(text);
    const searchMatch = text.trim().match(/^search(?:\s+for)?(?:\s+(.+))?$/i);
    if (searchMatch) {
      return { type: IntentType.SEARCH, searchQuery: searchMatch[1]?.trim(), rawText: text };
    }
    if (/^(?:please\s+)?print(?:\s+(?:this|the|current))?(?:\s+page)?(?:\s+please)?$/i.test(text.trim()) || /^(?:open|show)\s+(?:the\s+)?print\s+dialog$/i.test(text.trim())) {
      return { type: IntentType.PRINT, rawText: text };
    }

    const serialMatch = text.match(FORM_SERIAL_PATTERN);
    const serialNo = serialMatch ? serialMatch[0].toUpperCase().replace(/\s+/, '-') : undefined;
    const formTypeFromSerial = serialNo ? serialNo.split('-')[0] : undefined;

    let formType = formTypeFromSerial;
    if (!formType) {
      for (const [keyword, type] of Object.entries(FORM_TYPE_WORDS)) {
        if (this.hasKeyword(lower, keyword)) { formType = type; break; }
      }
    }

    let statusFilter: string | undefined;
    for (const [keyword, status] of Object.entries(STATUS_WORDS).sort((a, b) => b[0].length - a[0].length)) {
      if (lower.includes(keyword)) { statusFilter = status; break; }
    }
    const listSort = /\b(recent|recently|latest|newest|last)\b/.test(lower) ? 'recent' : undefined;
    const documentTypeQuery = this.extractDocumentTypeQuery(lower);
    const blMatch = lower.match(/\b(?:bl|beamline)\s*(\d+(?:\.\d+)*)\b/i);
    const impactQuestion = /\b(affect|affects|affected|affecting|impact|impacts|impacted|impacting)\b/.test(lower);
    const scheduleTypeQuery = this.extractScheduleTypeQuery(lower);
    const reviewBoard = /\bbrc\b|\bbeamline review committee\b/.test(lower)
      ? 'BRC'
      : /\barc\b|\baccelerator review committee\b/.test(lower) ? 'ARC' : undefined;

    if (serialNo && this.isSerialWaitingForMeQuestion(lower)) {
      return { type: IntentType.MY_SIGNATURE_STATUS, serialNo, formType, rawText: text };
    }
    if (serialNo && /\b(open|go to|take me|navigate|show me)\b/.test(lower)) {
      return { type: IntentType.NAVIGATE, serialNo, formType, rawText: text };
    }
    if (serialNo && /\b(stuck|blocking|who.*sign|why.*not|pending.*approv|approv.*pending|needs to sign)\b/.test(lower)) {
      return { type: IntentType.BLOCKING_INFO, serialNo, formType, rawText: text };
    }
    if (serialNo && /\b(status|where is|what.*status|progress|state|current)\b/.test(lower)) {
      return { type: IntentType.FORM_STATUS, serialNo, formType, rawText: text };
    }
    if (serialNo && !/(open|go to|take me|navigate|show me|stuck|blocking|who.*sign|pending|list|show all|show active)/.test(lower)) {
      return { type: IntentType.FORM_STATUS, serialNo, formType, rawText: text };
    }
    if (/\b(what can i do|what am i allowed|what access do i have|what permissions do i have|what privileges do i have|my permissions|my privileges|my roles|what are my roles)\b/.test(lower)) {
      return { type: IntentType.CAPABILITY_SUMMARY, rawText: text };
    }
    if (/\b(role|roles|permission|permissions|privilege|privileges|access)\b/.test(lower) && /\b(create|new|start|make|open)\b/.test(lower)) {
      const isChecklist = /\bchecklist|checklists\b/.test(lower);
      return { type: IntentType.PERMISSION_INFO, formType, action: 'create', isChecklist, rawText: text };
    }
    if (/\b(training|trainings)\b/.test(lower) && /\b(missing|needed|required|not approved|unapproved|pending approval|approval pending|approve|approval|how many|what|which|list|show|need|needs|due)\b/.test(lower)) {
      const trainingScope = /\b(i have to approve|to approve|need to approve|needs my approval|my approval|approve|approval queue|waiting for my approval)\b/.test(lower)
        ? 'approval'
        : /\b(not approved|unapproved|pending approval|approval pending)\b/.test(lower) ? 'pendingApproval' : 'missing';
      const countOnly = /\b(how many|count|number of)\b/.test(lower);
      return { type: IntentType.TRAINING_STATUS, trainingScope, countOnly, rawText: text };
    }
    if (reviewBoard && /\b(in progress|active|ongoing|underway)\b/.test(lower)) {
      return { type: IntentType.REVIEW_REQUESTS, reviewBoard, reviewScope: 'inProgress', rawText: text };
    }
    if (reviewBoard && /\b(request|requests)\b/.test(lower) && /\b(pending|waiting|list|show|what|which)\b/.test(lower)) {
      return { type: IntentType.REVIEW_REQUESTS, reviewBoard, reviewScope: 'pending', rawText: text };
    }
    // Schedule questions use server-calculated expiration status and dates from the shared schedule catalog.
    if (/\bschedules?\b/.test(lower) && /\b(expire|expires|expiring|expiration)\b/.test(lower) && /\b(next week|next 7 days|coming week)\b/.test(lower)) {
      return { type: IntentType.SCHEDULE_STATUS, scheduleScope: 'expiringNextWeek', scheduleTypeQuery, rawText: text };
    }
    if (/\bschedules?\b/.test(lower) && /\b(expired|overdue|past due)\b/.test(lower)) {
      return { type: IntentType.SCHEDULE_STATUS, scheduleScope: 'expired', scheduleTypeQuery, rawText: text };
    }
    if (blMatch && /\bschedul(?:e|ed|ing)\b/.test(lower) && /\b(checklist|review|when)\b/.test(lower)) {
      return {
        type: IntentType.SCHEDULE_STATUS,
        scheduleScope: 'resourceReview',
        resourceName: `BL ${blMatch[1]}`,
        resourceKind: 'beamline',
        rawText: text,
      };
    }
    if (blMatch && /\bschedules?\b/.test(lower) && /\b(date|dates|future|upcoming|coming|next|list|show|what|which)\b/.test(lower)) {
      return {
        type: IntentType.SCHEDULE_STATUS,
        scheduleScope: 'resourceSchedules',
        resourceName: `BL ${blMatch[1]}`,
        resourceKind: 'beamline',
        rawText: text,
      };
    }
    if (/\b(waiting to be signed for me|waiting on me|on my queue|my queue|need my signature|needs my signature|my signature|sign for me|need to sign|needs me to sign|waiting for me to sign|forms?.*signed for me|checklists?.*signed for me|what.*sign.*me|what.*waiting.*me)\b/.test(lower)) {
      return {
        type: IntentType.MY_SIGNATURES,
        signatureScope: this.resolveSignatureScope(lower),
        rawText: text,
      };
    }
    if (/\b(checklist template|template builder|builder template|builder|template)\b/.test(lower) && /\b(approval|approve|pending|need)\b/.test(lower)) {
      return { type: IntentType.MY_APPROVALS, approvalScope: 'templates', rawText: text };
    }
    if (/\b(catalog|catalogs)\b/.test(lower) && /\b(approval|approve|pending|need)\b/.test(lower)) {
      return { type: IntentType.MY_APPROVALS, approvalScope: 'catalogs', rawText: text };
    }
    if (/\b(forms?|checklists?)\b/.test(lower) && /\b(my approval|needs my|pending for me|waiting on me|i need to|what should i|my queue|my action|approve|approval|to approve)\b/.test(lower)) {
      return {
        type: IntentType.MY_SIGNATURES,
        signatureScope: this.resolveSignatureScope(lower),
        rawText: text,
      };
    }
    if (/\b(my approval|needs my|pending for me|waiting on me|i need to|what should i|my queue|my action|approve|to approve)\b/.test(lower)) {
      return { type: IntentType.MY_ACTIONS, rawText: text };
    }
    // Resolve impact questions before generic form lists so "affected by RSFs" keeps its resource direction.
    if (impactQuestion) {
      if (blMatch) {
        return {
          type: IntentType.AFFECTED_SYSTEMS,
          impactDirection: 'documentsByResource',
          resourceName: `BL ${blMatch[1]}`,
          resourceKind: 'beamline',
          formType,
          isChecklist: /\bchecklists?\b/.test(lower),
          statusFilter: 'Open',
          rawText: text,
        };
      }
      if (formType && /\b(beamlines?|branchlines?|accelerators?|resources?|systems?)\b/.test(lower)) {
        const resourceKind = /\bbranchlines?\b/.test(lower)
          ? 'branchline'
          : /\baccelerators?\b/.test(lower) ? 'accelerator' : 'beamline';
        return {
          type: IntentType.AFFECTED_SYSTEMS,
          impactDirection: 'resourcesByDocument',
          resourceKind,
          formType,
          statusFilter: 'Open',
          rawText: text,
        };
      }
    }
    if (formType && /\b(list|show|find|get|all|which|what|active|open|draft|closed|canceled|cancelled|completed|recent|recently|latest|newest)\b/.test(lower)) {
      const isChecklist = /\bchecklist\b/.test(lower);
      return { type: IntentType.LIST_FORMS, formType, documentTypeQuery, statusFilter, isChecklist, listSort, rawText: text };
    }
    if (/\b(checklist|checklists)\b/.test(lower) && /\b(list|show|find|get|all|which|what|active|open|draft|closed|canceled|cancelled|completed|recent|recently|latest|newest)\b/.test(lower)) {
      return { type: IntentType.LIST_FORMS, formType, documentTypeQuery, statusFilter, isChecklist: true, listSort, rawText: text };
    }
    if (/\b(list|show all|find all|all forms|open forms|active forms|draft forms|closed forms|recent forms|recently closed|latest forms|newest forms)\b/.test(lower)) {
      const isChecklist = /\bchecklist\b/.test(lower);
      return { type: IntentType.LIST_FORMS, formType, documentTypeQuery, statusFilter, isChecklist, listSort, rawText: text };
    }

    // Resource status: beamline "BL 5.0.2", branchline "1.4", accelerator "ALS"
    if (blMatch || /\bbeamline\b/.test(lower)) {
      const resourceName = blMatch ? `BL ${blMatch[1]}` : this.extractResourceName(text);
      return { type: IntentType.RESOURCE_STATUS, resourceName, resourceKind: 'beamline', rawText: text };
    }
    const branchMatch = text.match(/\bbranchline\s*([\d.]+)/i) ?? text.match(/\b(\d+\.\d+)\b/);
    if (/\bbranchline\b/.test(lower) || (branchMatch && !blMatch)) {
      const resourceName = branchMatch ? branchMatch[1] : this.extractResourceName(text);
      return { type: IntentType.RESOURCE_STATUS, resourceName, resourceKind: 'branchline', rawText: text };
    }
    if (/\baccelerator\b/.test(lower)) {
      const resourceName = this.extractResourceName(text);
      return { type: IntentType.RESOURCE_STATUS, resourceName, resourceKind: 'accelerator', rawText: text };
    }

    if (/\b(help|what can you|how do|commands|examples|capabilities|what do you)\b/.test(lower)) {
      return { type: IntentType.HELP, rawText: text };
    }

    return { type: IntentType.UNKNOWN, serialNo, formType, rawText: text };
  }

  /**
   * Sends locally unrecognized questions to the backend CBorg classifier and tracks whether AI interpretation is available.
   * @param text Question entered by the current user.
   * @returns The local intent or a validated CBorg intent.
   */
  resolveIntent(text: string): Observable<ParsedIntent> {
    const localIntent = this.parseIntent(text);
    if (localIntent.type !== IntentType.UNKNOWN) return of(localIntent);

    // CBorg sees only the question; ASCC data retrieval remains in the existing authorized handlers.
    return this.http.post<WorkflowAssistantInterpretResponse>(`${this.BASE_URL}/WorkflowAssistant/Interpret`, { question: text }).pipe(
      tap(response => {
        // Only consume an explicit availability value so mixed-version deployments remain compatible.
        this.recordCBorgAvailability(response?.available);
      }),
      map(response => response?.interpreted && response.intent
        ? { ...response.intent, rawText: text }
        : localIntent),
      catchError(() => {
        this.recordCBorgAvailability(false);
        return of(localIntent);
      }),
    );
  }

  /**
   * Stores explicit CBorg availability while treating an omitted value as intentionally unconfigured.
   * @param available Whether the backend completed a valid CBorg classification request.
   */
  private recordCBorgAvailability(available?: boolean): void {
    this.cborgAvailabilityChecked = true;
    if (available === false) this.cborgUnavailableSubject.next(true);
    if (available === true) this.cborgUnavailableSubject.next(false);
  }

  // ------------------------------------------------------------
  // Intent Processors
  // ------------------------------------------------------------

  /**
   * Resolves a structured intent through the existing ASCC stores and authorized API endpoints.
   * @param intent Validated local or CBorg intent.
   * @returns A display-ready assistant response.
   */
  processIntent(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    switch (intent.type) {
      case IntentType.FORM_STATUS: return this.handleFormStatus(intent);
      case IntentType.MY_APPROVALS: return this.handleMyApprovals(intent);
      case IntentType.MY_ACTIONS: return this.handleMyActions();
      case IntentType.MY_SIGNATURES: return this.handleMySignatures(intent);
      case IntentType.MY_SIGNATURE_STATUS: return this.handleMySignatureStatus(intent);
      case IntentType.BLOCKING_INFO: return this.handleBlockingInfo(intent);
      case IntentType.LIST_FORMS: return this.handleListForms(intent);
      case IntentType.AFFECTED_SYSTEMS: return this.handleAffectedSystems(intent);
      case IntentType.RESOURCE_STATUS: return this.handleResourceStatus(intent);
      case IntentType.NAVIGATE: return this.handleNavigate(intent);
      case IntentType.PERMISSION_INFO: return of(this.handlePermissionInfo(intent));
      case IntentType.CAPABILITY_SUMMARY: return of(this.handleCapabilitySummary());
      case IntentType.TRAINING_STATUS: return this.handleTrainingStatus(intent);
      case IntentType.SCHEDULE_STATUS: return this.handleScheduleStatus(intent);
      case IntentType.REVIEW_REQUESTS: return this.handleReviewRequests(intent);
      case IntentType.SEARCH: return of(this.handleSearch(intent));
      case IntentType.PRINT: return of(this.handlePrint());
      case IntentType.HELP: return of(this.helpResponse());
      default: return of(this.unknownResponse(intent.rawText));
    }
  }

  /**
   * Waits for the NGXS lists required by an intent while allowing non-data commands to continue immediately.
   * @param intent Parsed question whose data dependencies must be ready.
   */
  waitForStoreReadiness(intent: ParsedIntent): Observable<void> {
    // Poll lightweight in-memory flags; the chat typing indicator remains visible until the needed refresh succeeds.
    return timer(0, 100).pipe(
      filter(() => this.isStoreReadyForIntent(intent)),
      take(1),
      map(() => undefined),
    );
  }

  /**
   * Checks only the stores read by the supplied intent so Help, Search, and API-only questions are not delayed.
   * @param intent Parsed assistant intent.
   */
  private isStoreReadyForIntent(intent: ParsedIntent): boolean {
    const state = this.store.snapshot() as any;
    switch (intent.type) {
      case IntentType.MY_APPROVALS:
      case IntentType.MY_ACTIONS:
        return !!this.pendingService.pendingApprovals;
      case IntentType.FORM_STATUS:
      case IntentType.BLOCKING_INFO:
      case IntentType.LIST_FORMS:
      case IntentType.AFFECTED_SYSTEMS:
        return !!state.Documents?.loaded;
      case IntentType.PERMISSION_INFO:
      case IntentType.CAPABILITY_SUMMARY:
        return !!state.RolePrivileges?.loaded && !!state.Privileges?.loaded;
      case IntentType.TRAINING_STATUS:
        return !!state.MissingTraining?.loaded;
      case IntentType.SCHEDULE_STATUS:
        return !!state.Schedules?.loaded;
      case IntentType.REVIEW_REQUESTS:
        return !!state.ReviewCycles?.loaded && !!state.Reviews?.loaded;
      case IntentType.RESOURCE_STATUS:
        if (intent.resourceKind === 'beamline') return !!state.BeamlineStatus?.loaded;
        if (intent.resourceKind === 'branchline') return !!state.Branchlines?.loaded;
        if (intent.resourceKind === 'accelerator') return !!state.AcceleratorStatus?.loaded;
        return !!state.BeamlineStatus?.loaded && !!state.Branchlines?.loaded && !!state.AcceleratorStatus?.loaded;
      default:
        return true;
    }
  }

  // -- Application Actions -------------------------------------

  /**
   * Requests the standard ASCC search dialog with the user's search text prefilled.
   * @param intent Parsed search query.
   */
  private handleSearch(intent: ParsedIntent): Partial<ChatMessage> {
    const query = intent.searchQuery?.trim();
    return {
      content: query
        ? `Opening ASCC search for <strong>${this.toHtml(query)}</strong>.`
        : 'Opening ASCC search.',
      action: 'search',
      actionValue: query,
    };
  }

  /**
   * Requests the browser's native print dialog for the current ASCC view.
   */
  private handlePrint(): Partial<ChatMessage> {
    return { content: 'Opening the browser print dialog.', action: 'print' };
  }

  // -- Form Status ----------------------------------------------

  private handleFormStatus(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    const sn = intent.serialNo!;
    const safeSn = this.toHtml(sn);

    // Try NGXS documents store first (fast, no HTTP)
    const docs = this.store.selectSnapshot(DocumentsState.read);
    const doc = docs.find(d => (d.serialNo ?? '').toUpperCase() === sn.toUpperCase());
    if (doc) return of(this.formatDocumentStatus(doc));

    // Fallback: live HTTP for RSSWA (has getBySerialNo)
    const ft = intent.formType?.toUpperCase();
    if (ft === 'RSSWA' || sn.startsWith('RSSWA')) {
      return this.rsswaService.getBySerialNo(sn).pipe(
        map(form => {
          const statusName = this.toHtml(form.formStatus?.name ?? FormStatusEnum[form.status ?? 0]);
          const serialNo = this.toHtml(form.serialNo);
          const { cssClass } = this.statusDot(form.status);
          const lines = [
            `<span class="${cssClass}"><strong>● RSSWA ${serialNo}</strong></span>`,
            `<span class="grey">Status:</span> <strong>${statusName}</strong>`,
          ];
          if (form.requesterName) lines.push(`<span class="grey">Requested by:</span> ${this.toHtml(form.requesterName)}`);
          if (form.startOn) lines.push(`<span class="grey">Started:</span> ${this.fmtDate(form.startOn)}`);
          return { content: lines.join('<br>'), links: [{ label: `Open RSSWA ${form.serialNo}`, route: `/redirect/${form.serialNo}` }] as ChatLink[] };
        }),
        catchError(() => of({
          content: `<span class="red">⚠</span> Could not find RSSWA <strong>${safeSn}</strong>. Please verify the serial number.`,
          isError: true,
        })),
      );
    }

    // Generic fallback: fetch all documents and search
    return this.documentsService.getDocuments().pipe(
      map(forms => {
        const d = forms.find(f => (f.serialNo ?? '').toUpperCase() === sn.toUpperCase());
        return d ? this.formatDocumentStatus(d) : {
          content: `<span class="red">⚠</span> Could not find <strong>${safeSn}</strong>. Please verify the serial number.`,
          isError: true,
        };
      }),
      catchError(() => of({
        content: `<span class="red">⚠</span> Unable to retrieve data for <strong>${safeSn}</strong>. Please try again.`,
        isError: true,
      })),
    );
  }

  private formatDocumentStatus(doc: Document): Partial<ChatMessage> {
    const statusName = this.toHtml(doc.formStatus?.name ?? doc.statusName ?? FormStatusEnum[doc.status ?? 0]);
    const { cssClass } = this.statusDot(doc.status);
    const typeName = this.toHtml(doc.documentType?.name ?? '');
    const lines = [`<span class="${cssClass}"><strong>● ${this.toHtml(doc.serialNo)}</strong></span>`];
    if (typeName) lines.push(`<span class="grey">Type:</span> ${typeName}`);
    lines.push(`<span class="grey">Status:</span> <strong>${statusName}</strong>`);
    if (doc.requesterName) lines.push(`<span class="grey">Requested by:</span> ${this.toHtml(doc.requesterName)}`);
    if (doc.locationNames) lines.push(`<span class="grey">Location(s):</span> ${this.toHtml(doc.locationNames)}`);
    if (doc.createdOn) lines.push(`<span class="grey">Created:</span> ${this.fmtDate(doc.createdOn)}`);
    const links: ChatLink[] = doc.serialNo ? [{ label: `Open ${doc.serialNo}`, route: `/redirect/${doc.serialNo}` }] : [];
    return { content: lines.join('<br>'), links };
  }

  // -- Catalog / Template Approvals -----------------------------

  private handleMyActions(): Observable<Partial<ChatMessage>> {
    return this.http.get<WorkflowPendingSignature[]>(`${this.BASE_URL}/WorkFlowTask/PendingForMe`).pipe(
      map(items => {
        const signatures = items ?? [];
        const catalogTotal = this.pendingService.pendingApprovals?.total ?? 0;

        if (!catalogTotal && !signatures.length) {
          return {
            content: '<span class="green">✓</span> Nothing is waiting for your approval or signature right now.',
          };
        }

        const sections: string[] = [];
        const links: ChatLink[] = [];

        if (catalogTotal) {
          const catalog = this.formatMyApprovals('all');
          sections.push(catalog.content ?? '');
          links.push(...(catalog.links ?? []));
        }

        if (signatures.length) {
          sections.push(this.formatMySignatures(signatures, 'all').content ?? '');
        }

        return {
          content: sections.filter(Boolean).join('<br><br>'),
          links,
        };
      }),
      catchError(() => {
        const catalog = this.formatMyApprovals('all');
        if ((this.pendingService.pendingApprovals?.total ?? 0) > 0) {
          return of({
            content: `${catalog.content}<br><br><span class="red">⚠</span> Could not load workflow signatures waiting for you.`,
            links: catalog.links,
            isError: true,
          });
        }

        return of({
          content: '<span class="red">⚠</span> Could not load approvals or workflow signatures waiting for you.',
          isError: true,
        });
      }),
    );
  }

  private handleMyApprovals(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    return of(this.formatMyApprovals(intent.approvalScope ?? 'all'));
  }

  private formatMyApprovals(scope: ParsedIntent['approvalScope'] = 'all'): Partial<ChatMessage> {
    const pending = this.pendingService.pendingApprovals;

    if (!pending) {
      return {
        content: '<span class="red">⚠</span> Could not load catalog/template approvals. Make sure you are logged in.',
        isError: true,
      };
    }

    const lines: string[] = [];

    const sections: Array<{ label: string; items: any[] | undefined }> = [
      { label: 'Accelerator', items: pending.pendingAccelerators },
      { label: 'Resource/Beamline', items: pending.pendingResources },
      { label: 'User', items: pending.pendingUsers },
      { label: 'Role', items: pending.pendingRoles },
      { label: 'RSS DB', items: pending.pendingRSSDb },
      { label: 'Checklist Template', items: pending.pendingChecklistTemplates },
      { label: 'APL Category', items: pending.pendingAPLCategories },
      { label: 'APL Group', items: pending.pendingAPLGroups },
    ];
    const scopedSections = sections.filter(section => {
      if (scope === 'templates') return section.label === 'Checklist Template';
      if (scope === 'catalogs') return section.label !== 'Checklist Template';
      return true;
    });
    const total = scopedSections.reduce((sum, section) => sum + (section.items?.length ?? 0), 0);
    const scopeLabel = scope === 'templates'
      ? 'checklist template-builder'
      : scope === 'catalogs'
        ? 'catalog'
        : 'catalog/template';

    if (!total) {
      return { content: `<span class="green">✓</span> You have <strong>no ${scopeLabel} approvals</strong> right now.` };
    }

    lines.push(`<span class="blue"><strong>You have ${total} ${scopeLabel} item(s) needing approval:</strong></span>`);

    let shown = 0;
    for (const { label, items } of scopedSections) {
      if (!items?.length) continue;
      items.slice(0, 3).forEach(item => {
        if (shown < 8) {
          lines.push(`• <span class="grey">[${label}]</span> ${this.toHtml(item.name ?? 'Item')} – <span class="blue">${this.toHtml(item.actionName ?? item.statusName ?? '')}</span>`);
          shown++;
        }
      });
    }

    if (total > shown) {
      lines.push(`<span class="grey"><em>...and ${total - shown} more</em></span>`);
    }

    const links: ChatLink[] = [{ label: 'Go to Catalog/Template Approvals', route: '/pending-approvals' }];
    return { content: lines.join('<br>'), links };
  }

  // -- Procedure Training ---------------------------------------

  /**
   * Answers current-user procedure training questions from the report data.
   * @param intent Parsed training question.
   */
  private handleTrainingStatus(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    const userID = this.currentUser?.id;
    if (!userID) {
      return of({ content: '<span class="red">⚠</span> Could not identify your user account for training lookup.', isError: true });
    }

    if (intent.trainingScope === 'missing') {
      const missing = this.store.selectSnapshot((state: any) => state.MissingTraining?.data ?? []) as ProcedureReport[];
      if (missing.length) return of(this.formatTrainingList(missing, intent));
    }

    const reports$ = intent.trainingScope === 'approval'
      ? this.procedureReportService.read()
      : this.procedureReportService.readByUserID(userID);

    return reports$.pipe(
      map(reports => this.formatTrainingList(this.filterTrainingReports(reports ?? [], intent.trainingScope), intent)),
      catchError(() => of({
        content: '<span class="red">⚠</span> Could not load procedure training information right now.',
        isError: true,
      })),
    );
  }

  /**
   * Applies the assistant training scope to procedure reports.
   * @param reports Procedure report rows for the current user.
   * @param scope Training question scope.
   */
  private filterTrainingReports(reports: ProcedureReport[], scope: ParsedIntent['trainingScope']): ProcedureReport[] {
    if (scope === 'approval') return reports.filter(item => item.active && item.completedAndPending && this.canCurrentUserApproveTrainingReport(item));
    if (scope === 'pendingApproval') return reports.filter(item => item.active && item.completedAndPending);
    return reports.filter(item => item.active && item.revision != item.creditedRevision);
  }

  /**
   * Checks whether the current user appears responsible for approving a pending training report.
   * @param report Procedure report row to evaluate.
   */
  private canCurrentUserApproveTrainingReport(report: ProcedureReport): boolean {
    if (this.hasPrivilege(PrivilegeEnum.ALSProcedureTrainingAdmin) || this.hasRoles([Roles.SA, Roles.PC])) return true;

    const currentName = (this.currentUser?.name ?? '').toLowerCase();
    if (!currentName) return false;

    return [
      report.approverName,
      report.groupManagers,
      report.categoryManagers,
    ].some(value => (value ?? '').toLowerCase().includes(currentName));
  }

  /**
   * Formats missing or pending-approval procedure training rows.
   * @param trainings Procedure report rows to summarize.
   * @param intent Parsed training intent.
   */
  private formatTrainingList(trainings: ProcedureReport[], intent: ParsedIntent): Partial<ChatMessage> {
    const pendingApproval = intent.trainingScope === 'pendingApproval';
    const approval = intent.trainingScope === 'approval';
    const pendingCredit = pendingApproval || approval;
    const label = approval ? 'training approval(s)' : pendingApproval ? 'training(s) not approved yet' : 'missing training(s)';
    const route = '/procedure-training';

    if (!trainings.length) {
      return {
        content: `<span class="green">✓</span> You have <strong>no ${label}</strong>.`,
        links: [{ label: 'Open Procedure Training', route }],
      };
    }

    if (intent.countOnly) {
      return {
        content: `<span class="blue"><strong>${trainings.length}</strong></span> ${label} found.`,
        links: [{ label: 'Open Procedure Training', route }],
      };
    }

    const lines = [`<span class="blue"><strong>You have ${trainings.length} ${label}:</strong></span>`];
    trainings.slice(0, 10).forEach(item => {
      const revision = pendingCredit
        ? `Rev. ${this.toHtml(item.revision)}`
        : `Rev. ${this.toHtml(item.creditedRevision || 'None')} → ${this.toHtml(item.revision)}`;
      const user = approval ? ` <span class="grey">for ${this.toHtml(item.userName)}</span>` : '';
      lines.push(`• <strong>${this.inlineRouteLink(item.procedureNumber, route)}</strong> — ${this.toHtml(item.title)}${user} <span class="grey">(${revision})</span>`);
    });
    if (trainings.length > 10) lines.push(`<span class="grey"><em>...and ${trainings.length - 10} more</em></span>`);

    return { content: lines.join('<br>'), links: [{ label: 'Open Procedure Training', route }] };
  }

  private handleMySignatures(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    return this.http.get<WorkflowPendingSignature[]>(`${this.BASE_URL}/WorkFlowTask/PendingForMe`).pipe(
      map(items => {
        const scope = intent.signatureScope ?? 'all';
        return this.formatMySignatures(this.filterSignatureScope(items ?? [], scope), scope);
      }),
      catchError(() => of({
        content: '<span class="red">⚠</span> Could not load workflow signatures waiting for you.',
        isError: true,
      })),
    );
  }

  private handleMySignatureStatus(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    const serialNo = intent.serialNo!;

    return this.http.get<WorkflowPendingSignature[]>(`${this.BASE_URL}/WorkFlowTask/PendingForMe`).pipe(
      map(items => {
        const matching = (items ?? []).filter(item => this.sameSerial(item.serialNo, serialNo));
        return this.formatSerialSignatureStatus(serialNo, matching);
      }),
      catchError(() => of({
        content: `<span class="red">⚠</span> Could not check whether <strong>${this.toHtml(serialNo)}</strong> is waiting for you.`,
        isError: true,
      })),
    );
  }

  private filterSignatureScope(items: WorkflowPendingSignature[], scope: ParsedIntent['signatureScope'] = 'all'): WorkflowPendingSignature[] {
    if (scope === 'checklists') {
      return items.filter(item => item.documentCategory === DocumentCategory.Checklist);
    }

    if (scope === 'forms') {
      return items.filter(item => item.documentCategory === DocumentCategory.Form);
    }

    return items;
  }

  private formatMySignatures(items: WorkflowPendingSignature[], scope: ParsedIntent['signatureScope'] = 'all'): Partial<ChatMessage> {
    const target = scope === 'forms' ? 'forms' : scope === 'checklists' ? 'checklists' : 'forms or checklists';
    if (!items.length) {
      return {
        content: `<span class="green">✓</span> No ${target} are waiting for your signature right now.`,
      };
    }

    const grouped = items.reduce((acc, item) => {
      const serial = item.serialNo ?? 'Unknown';
      if (!acc[serial]) acc[serial] = [];
      acc[serial].push(item);
      return acc;
    }, {} as Record<string, WorkflowPendingSignature[]>);

    const serials = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    const shownItems: WorkflowPendingSignature[] = [];
    const lines = [
      `<span class="blue"><strong>${items.length} workflow signature step(s)</strong></span> are waiting for you across <strong>${serials.length}</strong> ${target} item(s):`,
    ];

    for (const serial of serials) {
      const first = grouped[serial][0];
      const typeName = first.formTypeName || first.documentTypeName || 'Workflow';
      const status = first.statusName ? ` <span class="grey">(${this.toHtml(first.statusName)})</span>` : '';
      const route = first.route || `/redirect/${serial}`;
      lines.push(`• <strong>${this.inlineRouteLink(serial, route)}</strong> <span class="grey">[${this.toHtml(typeName)}]</span>${status}`);

      grouped[serial].slice(0, 2).forEach(step => {
        if (shownItems.length >= 10) return;
        shownItems.push(step);
        const section = step.sectionName ? `<span class="grey">${this.toHtml(step.sectionName)}:</span> ` : '';
        const signers = [...(step.roleNames ?? []), ...(step.userNames ?? [])].filter(Boolean).join(', ');
        const signerText = signers ? ` — <span class="blue">${this.toHtml(signers)}</span>` : '';
        lines.push(`  ${section}<strong>${this.toHtml(step.taskName || 'Signature')}</strong>${signerText}`);
      });

      if (shownItems.length >= 10) break;
    }

    if (items.length > shownItems.length) {
      lines.push(`<span class="grey"><em>…and ${items.length - shownItems.length} more signature step(s)</em></span>`);
    }

    return { content: lines.join('<br>') };
  }

  private formatSerialSignatureStatus(serialNo: string, items: WorkflowPendingSignature[]): Partial<ChatMessage> {
    const safeSerial = this.toHtml(serialNo);

    if (!items.length) {
      return {
        content: `<span class="green">✓</span> <strong>${safeSerial}</strong> is not waiting for your signature right now.`,
      };
    }

    const first = items[0];
    const route = first.route || `/redirect/${serialNo}`;
    const typeName = first.formTypeName || first.documentTypeName || 'Workflow';
    const status = first.statusName ? ` <span class="grey">(${this.toHtml(first.statusName)})</span>` : '';
    const lines = [
      `<span class="yellow">▶</span> Yes, <strong>${this.inlineRouteLink(serialNo, route)}</strong> <span class="grey">[${this.toHtml(typeName)}]</span>${status} is waiting for you:`,
    ];

    items.slice(0, 5).forEach(step => {
      const section = step.sectionName ? `<span class="grey">${this.toHtml(step.sectionName)}:</span> ` : '';
      const signers = [...(step.roleNames ?? []), ...(step.userNames ?? [])].filter(Boolean).join(', ');
      const signerText = signers ? ` — <span class="blue">${this.toHtml(signers)}</span>` : '';
      lines.push(`• ${section}<strong>${this.toHtml(step.taskName || 'Signature')}</strong>${signerText}`);
    });

    if (items.length > 5) {
      lines.push(`<span class="grey"><em>…and ${items.length - 5} more signature step(s)</em></span>`);
    }

    return { content: lines.join('<br>') };
  }

  // -- Blocking Info --------------------------------------------

  private handleBlockingInfo(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    const sn = intent.serialNo!;
    const safeSn = this.toHtml(sn);
    const ft = (intent.formType ?? sn.split('-')[0]).toUpperCase();

    // Fast path: check store for terminal status (no HTTP needed)
    const docs = this.store.selectSnapshot(DocumentsState.read);
    const storeDoc = docs.find(d => (d.serialNo ?? '').toUpperCase() === sn.toUpperCase());
    const terminalStatuses = [FormStatusEnum.Closed, FormStatusEnum.Canceled, FormStatusEnum.WorkCompleted];
    if (storeDoc && terminalStatuses.includes(storeDoc.status as FormStatusEnum)) {
      const statusName = storeDoc.formStatus?.name ?? storeDoc.statusName ?? FormStatusEnum[storeDoc.status ?? 0];
      const cls = storeDoc.status === FormStatusEnum.Canceled ? 'red' : 'grey';
      return of({
        content: `<span class="${cls}">●</span> <strong>${safeSn}</strong> is <strong>${this.toHtml(statusName)}</strong> and is not stuck — no pending workflow steps.`,
        links: [{ label: `Open ${sn}`, route: `/redirect/${sn}` }],
      });
    }

    // RSSWA: dedicated service has getBySerialNo returning full wfTable
    if (ft === 'RSSWA') {
      return this.rsswaService.getBySerialNo(sn).pipe(
        map(form => {
          const statusName = form.formStatus?.name ?? '';
          const terminalNames = ['Closed', 'Canceled', 'Work Completed'];
          if (terminalNames.includes(statusName)) {
            const cls = statusName === 'Canceled' ? 'red' : 'grey';
            return {
              content: `<span class="${cls}">●</span> <strong>${safeSn}</strong> is <strong>${this.toHtml(statusName)}</strong> and is not stuck — no pending workflow steps.`,
              links: [{ label: `Open ${sn}`, route: `/redirect/${sn}` }],
            };
          }
          if (!form.wfTable) {
            return { content: `<span class="blue">ℹ</span> <strong>${safeSn}</strong> has no active workflow. Status: <strong>${this.toHtml(statusName || 'Unknown')}</strong>.` };
          }
          return this.extractPendingSignatures(sn, form.wfTable.wfTableLocal?.wfSectionLocals ?? [], ft);
        }),
        catchError(() => of({
          content: `<span class="red">⚠</span> Could not find <strong>${safeSn}</strong>.`,
          isError: true,
        })),
      );
    }

    // Other form types: use formID from store + HTTP fetch to get wfTable
    const formID = storeDoc?.formID;
    const endpoint = this.formTypeToEndpoint(ft);

    if (!endpoint || !formID) {
      return of({
        content: `<span class="blue">ℹ</span> To check what is blocking <strong>${safeSn}</strong>, open the form directly.`,
        links: [{ label: `Open ${sn}`, route: `/redirect/${sn}` }],
      });
    }

    return this.http.get<any>(`${this.BASE_URL}/${endpoint}/${formID}`).pipe(
      map(form => {
        const statusName = form.formStatus?.name ?? '';
        const terminalNames = ['Closed', 'Canceled', 'Work Completed'];
        if (terminalNames.includes(statusName)) {
          const cls = statusName === 'Canceled' ? 'red' : 'grey';
          return {
            content: `<span class="${cls}">●</span> <strong>${safeSn}</strong> is <strong>${this.toHtml(statusName)}</strong> and is not stuck.`,
            links: [{ label: `Open ${sn}`, route: `/redirect/${sn}` }],
          };
        }
        if (!form.wfTable) {
          return { content: `<span class="blue">ℹ</span> <strong>${safeSn}</strong> has no active workflow.` };
        }
        return this.extractPendingSignatures(sn, form.wfTable?.wfTableLocal?.wfSectionLocals ?? [], ft);
      }),
      catchError(() => of({
        content: `<span class="red">⚠</span> Could not retrieve workflow data for <strong>${safeSn}</strong>.`,
        isError: true,
      })),
    );
  }

  private extractPendingSignatures(sn: string, sections: WFSectionLocalResource[], formType?: string): Partial<ChatMessage> {
    const pending: { taskName: string; sectionName: string; signers: string; order: number }[] = [];
    const isScf = formType?.toUpperCase() === 'SCF';

    for (const section of sections) {
      const sigs: WfSignatureResource[] = section.wfSignatures ?? [];
      for (const sig of sigs) {
        // Already signed
        if (sig.signed) continue;

        const stepCode = sig.wfTaskLocal?.wfStepLocal?.code;

        // Holdpoint steps: only include if an actual hold point was created for this task
        if (stepCode === 'HOLDPOINT' && !sig.holdPointID) continue;

        if (isScf) {
          if (!this.isPendingScfSignature(sig, stepCode)) continue;
        } else {
          // Not enabled for the current form status
          if (!sig.enabled) continue;

          // Check the underlying WfTask required/enabled flags
          const wfTask = sig.wfTaskLocal?.wfTasks?.[0];
          if (wfTask) {
            if (wfTask.enabled === false) continue;
            if (wfTask.required === false) continue;
            // If already signed at the task level
            if (wfTask.signedBy != null) continue;
          }

          // Skip if the signature itself is marked not required
          if (sig.required === false) continue;
        }

        const roleNames = (sig.roles ?? []).map(r => r.name).filter((n): n is string => !!n);
        const userNames = (sig.users ?? []).map(u => u.name).filter((n): n is string => !!n);
        const signers = [...roleNames, ...userNames].join(' / ') || 'assigned signer';
        const taskName = sig.titleBefore ?? 'Signature';
        const secName = sig.sectionName ?? section.name ?? '';
        const order = (sig.sectionNumber ?? 0) * 1000 + (sig.order ?? 0);
        pending.push({ taskName, sectionName: secName, signers, order });
      }
    }

    // Sort by section/step order so the most immediate step appears first
    pending.sort((a, b) => a.order - b.order);

    if (!pending.length) {
      return {
        content: `<span class="green">✓</span> No pending signature steps found for <strong>${this.toHtml(sn)}</strong>. The workflow may be complete or not yet started.`,
        links: [{ label: `Open ${sn}`, route: `/redirect/${sn}` }],
      };
    }

    const lines = [`<span class="yellow">▶</span> <strong>${this.toHtml(sn)}</strong> is waiting for:`];
    pending.slice(0, 6).forEach(p => {
      const sec = p.sectionName ? `<span class="grey">[${this.toHtml(p.sectionName)}]</span> ` : '';
      lines.push(`• ${sec}<strong>${this.toHtml(p.taskName)}</strong> — <span class="blue">${this.toHtml(p.signers)}</span>`);
    });
    if (pending.length > 6) {
      lines.push(`<span class="grey"><em>...and ${pending.length - 6} more step(s)</em></span>`);
    }

    return {
      content: lines.join('<br>'),
      links: [{ label: `Open ${sn}`, route: `/redirect/${sn}` }],
    };
  }

  private isPendingScfSignature(sig: WfSignatureResource, stepCode?: string): boolean {
    if (stepCode === 'NO_WORK') return false;

    // SCF operational approval checkboxes gate which review signatures are relevant.
    // The SCF section 4 component uses `value` as the checkbox-selected flag.
    if (sig.sectionNumber === 4 && sig.value !== true) return false;

    if (sig.approveEnabled) return true;

    // "Shielding inspected" is an Awaiting Inspection FO step. It can be the next
    // blocking signature even when generic task-level flags make it look complete.
    const title = (sig.titleBefore ?? '').toLowerCase();
    if (sig.code === 'SHIELDINGINSPECTED2' || title.includes('shielding inspected')) return true;

    return false;
  }

  private formTypeToEndpoint(ft: string): string | null {
    const map: Record<string, string> = {
      SCF: 'SCFMasterV2',
      KE: 'KEnable',
      RSSWA: 'RSSWA',
      PPSTB: 'PPSTB',
      RSF: 'RSF',
      EB: 'EB',
      EBF: 'EB',
      ABSI: 'Absi',
    };
    return map[ft] ?? null;
  }

  // -- List Forms -----------------------------------------------

  private handleListForms(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    const docs = this.store.selectSnapshot(DocumentsState.read);

    if (!docs.length) {
      return this.documentsService.getDocuments().pipe(
        map(fresh => this.buildFormList(fresh, intent)),
        catchError(() => of({ content: '<span class="red">⚠</span> Could not load documents right now.', isError: true })),
      );
    }

    return of(this.buildFormList(docs, intent));
  }

  private buildFormList(docs: Document[], intent: ParsedIntent): Partial<ChatMessage> {
    const ft = intent.formType;
    const isChecklist = intent.isChecklist ?? false;
    const statusFilter = intent.statusFilter;
    const documentTypeQuery = intent.documentTypeQuery;

    const typeFilter = ft ? this.formTypeStringToEnum(ft) : null;
    const statusIds = statusFilter ? this.statusNameToIds(statusFilter) : null;
    const categoryFilter = isChecklist ? DocumentCategory.Checklist : DocumentCategory.Form;

    const filtered = docs.filter(d => {
      if ((d.documentType?.category ?? d.category) !== categoryFilter) return false;
      if (typeFilter !== null && !this.documentMatchesType(d, typeFilter, ft)) return false;
      if (typeFilter === null && documentTypeQuery && !this.documentMatchesTypeQuery(d, documentTypeQuery)) return false;
      if (statusIds && !this.documentMatchesStatus(d, statusIds, statusFilter)) return false;
      return true;
    }).sort((a, b) => this.sortDocuments(a, b, intent.listSort));

    const label = statusFilter ? this.getStatusDisplayName(statusFilter) + ' ' : '';
    const typeName = ft ?? documentTypeQuery ?? (isChecklist ? 'checklist' : 'form');
    const sortedLabel = intent.listSort === 'recent' ? 'most recent ' : '';

    if (!filtered.length) {
      return { content: `<span class="grey">No ${this.toHtml(label)}${this.toHtml(typeName)}(s) found.</span>` };
    }

    const lines = [`<span class="blue"><strong>${filtered.length} ${this.toHtml(sortedLabel)}${this.toHtml(label)}${this.toHtml(typeName)}(s):</strong></span>`];
    filtered.slice(0, 10).forEach(d => {
      const { cssClass } = this.statusDot(d.status);
      const statusLabel = this.toHtml(d.formStatus?.name ?? d.statusName ?? 'Unknown');
      const docType = d.documentType?.name ? ` <span class="grey">[${this.toHtml(d.documentType.name)}]</span>` : '';
      const date = this.getDocumentSortDate(d);
      const dateText = intent.listSort === 'recent' && date ? ` <span class="grey">(${this.fmtDate(date)})</span>` : '';
      lines.push(`• <span class="${cssClass}">●</span> <strong>${this.inlineRouteLink(d.serialNo ?? 'N/A', `/redirect/${d.serialNo}`)}</strong>${docType} – <span class="grey">${statusLabel}</span>${dateText}`);
    });
    if (filtered.length > 10) lines.push(`<span class="grey"><em>...and ${filtered.length - 10} more</em></span>`);

    const route = ft ? this.routeForFormTypeEnum(typeFilter) : '/home';
    const linkLabel = `Open ${ft ?? 'All'} ${isChecklist ? 'checklists' : 'forms'}`;
    return { content: lines.join('<br>'), links: [{ label: linkLabel, route }] };
  }

  // -- Affected Systems -----------------------------------------

  /**
   * Answers current document-to-resource impact questions from the shared document catalog.
   * @param intent Parsed impact direction, document type, resource, and current-status filter.
   */
  private handleAffectedSystems(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    const docs = this.store.selectSnapshot(DocumentsState.read);

    if (!docs.length) {
      return this.documentsService.getDocuments().pipe(
        map(fresh => this.buildAffectedSystemsResponse(fresh, intent)),
        catchError(() => of({ content: '<span class="red">⚠</span> Could not load affected-system information right now.', isError: true })),
      );
    }

    return of(this.buildAffectedSystemsResponse(docs, intent));
  }

  /**
   * Filters current documents and formats either documents for one resource or resources for one form type.
   * @param docs Documents available to the current ASCC user.
   * @param intent Parsed affected-system question.
   */
  private buildAffectedSystemsResponse(docs: Document[], intent: ParsedIntent): Partial<ChatMessage> {
    const typeFilter = intent.formType ? this.formTypeStringToEnum(intent.formType) : null;
    // Affected-system answers represent current operational impact and must never include inactive forms.
    const statusFilter = 'Open';
    const statusIds = this.statusNameToIds(statusFilter);
    const category = intent.isChecklist ? DocumentCategory.Checklist : DocumentCategory.Form;
    const documents = docs.filter(document => {
      if ((document.documentType?.category ?? document.category) !== category) return false;
      if (typeFilter !== null && !this.documentMatchesType(document, typeFilter, intent.formType)) return false;
      if (statusIds && !this.documentMatchesStatus(document, statusIds, statusFilter)) return false;
      return true;
    });

    if (intent.impactDirection === 'documentsByResource') {
      return this.formatDocumentsAffectingResource(documents, intent);
    }

    return this.formatResourcesAffectedByDocuments(documents, intent);
  }

  /**
   * Lists documents whose structured affected locations exactly match the requested resource.
   * @param documents Status- and type-filtered documents.
   * @param intent Parsed resource impact question.
   */
  private formatDocumentsAffectingResource(documents: Document[], intent: ParsedIntent): Partial<ChatMessage> {
    const resourceName = intent.resourceName ?? '';
    const expectedType = this.resourceKindToType(intent.resourceKind);
    const matches = documents
      .filter(document => this.getDocumentLocations(document).some(location =>
        (location.type == null || expectedType === null || location.type === expectedType) &&
        this.resourceNamesMatch(location.name, resourceName)
      ))
      .sort((left, right) => this.sortDocuments(left, right, 'recent'));
    const currentLabel = ' open';
    const typeLabel = intent.formType ? ` ${intent.formType}` : '';

    if (!matches.length) {
      return { content: `<span class="grey">No${this.toHtml(currentLabel)}${this.toHtml(typeLabel)} form(s) are affecting <strong>${this.toHtml(resourceName)}</strong>.</span>` };
    }

    const lines = [`<span class="blue"><strong>${matches.length}${this.toHtml(currentLabel)}${this.toHtml(typeLabel)} form(s) affecting ${this.toHtml(resourceName)}:</strong></span>`];
    matches.slice(0, 10).forEach(document => {
      const status = this.toHtml(document.formStatus?.name ?? document.statusName ?? 'Unknown');
      const type = document.documentType?.name ? ` <span class="grey">[${this.toHtml(document.documentType.name)}]</span>` : '';
      lines.push(`• <strong>${this.inlineRouteLink(document.serialNo ?? 'N/A', `/redirect/${document.serialNo}`)}</strong>${type} – <span class="grey">${status}</span>`);
    });
    if (matches.length > 10) lines.push(`<span class="grey"><em>...and ${matches.length - 10} more</em></span>`);

    return { content: lines.join('<br>') };
  }

  /**
   * Groups affected resources by name and includes links to the matching documents.
   * @param documents Status- and type-filtered documents.
   * @param intent Parsed document impact question.
   */
  private formatResourcesAffectedByDocuments(documents: Document[], intent: ParsedIntent): Partial<ChatMessage> {
    const expectedType = this.resourceKindToType(intent.resourceKind);
    const grouped = new Map<string, { name: string; documents: Document[] }>();

    documents.forEach(document => {
      this.getDocumentLocations(document)
        .filter(location => expectedType === null || location.type === expectedType)
        .forEach(location => {
          const key = this.normalizeResourceName(location.name);
          if (!key) return;
          const existing = grouped.get(key) ?? { name: location.name ?? '', documents: [] };
          if (!existing.documents.some(item => item.id === document.id)) existing.documents.push(document);
          grouped.set(key, existing);
        });
    });

    const resources = Array.from(grouped.values()).sort((left, right) => left.name.localeCompare(right.name, undefined, { numeric: true }));
    const kindLabel = intent.resourceKind ?? 'resource';
    const currentLabel = 'open ';
    const formLabel = intent.formType ?? 'form';

    if (!resources.length) {
      return { content: `<span class="grey">No ${this.toHtml(kindLabel)}s are affected by ${this.toHtml(currentLabel)}${this.toHtml(formLabel)} form(s).</span>` };
    }

    const lines = [`<span class="blue"><strong>${resources.length} ${this.toHtml(kindLabel)}(s) affected by ${this.toHtml(currentLabel)}${this.toHtml(formLabel)} form(s):</strong></span>`];
    resources.slice(0, 15).forEach(resource => {
      const documentLinks = resource.documents
        .sort((left, right) => this.sortDocuments(left, right, 'recent'))
        .map(document => this.inlineRouteLink(document.serialNo ?? 'N/A', `/redirect/${document.serialNo}`))
        .join(', ');
      lines.push(`• <strong>${this.toHtml(resource.name)}</strong> — ${documentLinks}`);
    });
    if (resources.length > 15) lines.push(`<span class="grey"><em>...and ${resources.length - 15} more</em></span>`);

    return { content: lines.join('<br>') };
  }

  /**
   * Returns structured locations, falling back to comma-separated names for older document rows.
   * @param document Document catalog row.
   */
  private getDocumentLocations(document: Document): Array<{ name?: string | null; type?: number | null }> {
    if (document.locations?.length) return document.locations;
    return (document.locationNames ?? '').split(',').map(name => ({ name: name.trim() })).filter(location => !!location.name);
  }

  /**
   * Compares resource names without allowing partial numeric beamline matches.
   * @param left Catalog resource name.
   * @param right User-requested resource name.
   */
  private resourceNamesMatch(left?: string | null, right?: string | null): boolean {
    const leftName = this.normalizeResourceName(left);
    const rightName = this.normalizeResourceName(right);
    return !!leftName && leftName === rightName;
  }

  /**
   * Canonicalizes display variants such as "Beamline 3.2.1" and "BL 3.2.1" for exact comparison.
   * @param value Resource display name.
   */
  private normalizeResourceName(value?: string | null): string {
    return (value ?? '').toLowerCase().replace(/^(?:beamline|bl)\s*/, '').replace(/\s+/g, '');
  }

  /**
   * Maps assistant resource wording to the catalog resource type used by document locations.
   * @param kind Parsed resource kind.
   */
  private resourceKindToType(kind?: ParsedIntent['resourceKind']): ResourceType | null {
    const types: Partial<Record<NonNullable<ParsedIntent['resourceKind']>, ResourceType>> = {
      beamline: ResourceType.Beamline,
      branchline: ResourceType.Branchline,
      accelerator: ResourceType.Accelerator,
    };
    return kind ? types[kind] ?? null : null;
  }

  // -- Schedules ------------------------------------------------

  /**
   * Answers expiration, resource review, and open resource schedule questions from the shared schedule catalog.
   * @param intent Parsed schedule scope, type filter, and optional resource.
   */
  private handleScheduleStatus(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    const schedules = this.store.selectSnapshot(SchedulesState.read) ?? [];

    if (!schedules.length) {
      return this.scheduleService.getAll().pipe(
        map(fresh => this.buildScheduleStatusResponse(fresh, intent)),
        catchError(() => of({ content: '<span class="red">⚠</span> Could not load schedule information right now.', isError: true })),
      );
    }

    return of(this.buildScheduleStatusResponse(schedules, intent));
  }

  /**
   * Applies schedule type and lifecycle filters before formatting the requested schedule view.
   * @param schedules Schedules available from ASCC.
   * @param intent Parsed schedule question.
   */
  private buildScheduleStatusResponse(schedules: Schedule[], intent: ParsedIntent): Partial<ChatMessage> {
    const activeSchedules = schedules
      .filter(schedule => schedule.isActive !== false)
      .filter(schedule => schedule.statusId !== ScheduleStatusEnum.completed && schedule.statusId !== ScheduleStatusEnum.completedSubdetail && schedule.statusId !== ScheduleStatusEnum.remove)
      .filter(schedule => schedule.statusTime !== ScheduleStatusTimeEnum.Completed && schedule.statusTime !== ScheduleStatusTimeEnum.Archived)
      .filter(schedule => this.scheduleMatchesType(schedule, intent.scheduleTypeQuery));

    if (intent.scheduleScope === 'resourceReview') {
      return this.formatResourceReviewSchedules(activeSchedules, intent);
    }

    if (intent.scheduleScope === 'resourceSchedules') {
      return this.formatResourceSchedules(activeSchedules, intent);
    }

    if (intent.scheduleScope === 'expiringNextWeek') {
      const tomorrow = this.addDays(this.startOfDay(new Date()), 1);
      const rangeEnd = this.addDays(tomorrow, 7);
      const expiring = activeSchedules
        .filter(schedule => {
          const expiration = this.getScheduleExpirationDate(schedule);
          return expiration != null && expiration >= tomorrow && expiration < rangeEnd;
        })
        .sort((left, right) => this.getScheduleExpirationTime(left) - this.getScheduleExpirationTime(right));
      return this.formatScheduleList(expiring, intent, 'expiring in the next 7 days', 'Expires');
    }

    const expired = activeSchedules
      .filter(schedule => schedule.statusTime === ScheduleStatusTimeEnum.Expired)
      .sort((left, right) => this.getScheduleExpirationTime(left) - this.getScheduleExpirationTime(right));
    return this.formatScheduleList(expired, intent, 'expired', 'Expired');
  }

  /**
   * Formats schedule expiration results with schedule type, resource, and expiration date.
   * @param schedules Filtered schedules.
   * @param intent Parsed schedule question.
   * @param heading Status phrase shown in the result heading.
   * @param dateLabel Label for the expiration date.
   */
  private formatScheduleList(schedules: Schedule[], intent: ParsedIntent, heading: string, dateLabel: string): Partial<ChatMessage> {
    const typeLabel = intent.scheduleTypeQuery ? ` ${intent.scheduleTypeQuery}` : '';
    if (!schedules.length) {
      return { content: `<span class="grey">No${this.toHtml(typeLabel)} schedules are ${this.toHtml(heading)}.</span>` };
    }

    const lines = [`<span class="blue"><strong>${schedules.length}${this.toHtml(typeLabel)} schedule(s) ${this.toHtml(heading)}:</strong></span>`];
    schedules.slice(0, 15).forEach(schedule => {
      const scheduleName = this.getScheduleDisplayName(schedule);
      const scheduleLink = schedule.type?.id
        ? this.inlineRouteLink(scheduleName, `/schedulesV2/${schedule.type.id}`)
        : this.toHtml(scheduleName);
      const resources = this.getScheduleResourceNames(schedule).map(name => this.toHtml(name)).join(', ') || 'No resource assigned';
      const expiration = this.getScheduleExpirationDate(schedule);
      const dateText = expiration ? ` — <span class="grey">${dateLabel} ${this.fmtDate(expiration)}</span>` : '';
      lines.push(`• <strong>${scheduleLink}</strong> — ${resources}${dateText}`);
    });
    if (schedules.length > 15) lines.push(`<span class="grey"><em>...and ${schedules.length - 15} more</em></span>`);

    return { content: lines.join('<br>') };
  }

  /**
   * Lists active review schedules for one resource, preferring an explicit scheduled date over the next due date.
   * @param schedules Active schedules matching any requested schedule type.
   * @param intent Parsed resource review question.
   */
  private formatResourceReviewSchedules(schedules: Schedule[], intent: ParsedIntent): Partial<ChatMessage> {
    const resourceName = intent.resourceName ?? '';
    const matches = schedules
      .filter(schedule => this.getScheduleResourceNames(schedule).some(name => this.resourceNamesMatch(name, resourceName)))
      .filter(schedule => schedule.scheduleFor != null || schedule.nextDue != null)
      .sort((left, right) => this.getScheduleReviewTime(left) - this.getScheduleReviewTime(right));

    if (!matches.length) {
      return { content: `<span class="grey">No active checklist review schedule was found for <strong>${this.toHtml(resourceName)}</strong>.</span>` };
    }

    const lines = [`<span class="blue"><strong>Checklist review schedule(s) for ${this.toHtml(resourceName)}:</strong></span>`];
    matches.slice(0, 10).forEach(schedule => {
      const scheduleName = this.getScheduleDisplayName(schedule);
      const scheduleLink = schedule.type?.id
        ? this.inlineRouteLink(scheduleName, `/schedulesV2/${schedule.type.id}`)
        : this.toHtml(scheduleName);
      const date = schedule.scheduleFor ?? schedule.nextDue;
      const dateLabel = schedule.scheduleFor ? 'Scheduled for' : 'Next review due';
      lines.push(`• <strong>${scheduleLink}</strong> — ${this.toHtml(resourceName)} — <span class="grey">${dateLabel} ${this.fmtDate(date)}</span>`);
    });
    if (matches.length > 10) lines.push(`<span class="grey"><em>...and ${matches.length - 10} more</em></span>`);

    return { content: lines.join('<br>') };
  }

  /**
   * Lists every open schedule for one resource, including good, grace, expired, and extended schedules.
   * @param schedules Active schedules matching any requested schedule type.
   * @param intent Parsed resource schedule question.
   */
  private formatResourceSchedules(schedules: Schedule[], intent: ParsedIntent): Partial<ChatMessage> {
    const resourceName = intent.resourceName ?? '';
    const matches = schedules
      .filter(schedule => this.getScheduleResourceNames(schedule).some(name => this.resourceNamesMatch(name, resourceName)))
      .sort((left, right) => this.getScheduleDateSortTime(left) - this.getScheduleDateSortTime(right));

    if (!matches.length) {
      return { content: `<span class="grey">No open schedules were found for <strong>${this.toHtml(resourceName)}</strong>.</span>` };
    }

    const lines = [`<span class="blue"><strong>Open schedules for ${this.toHtml(resourceName)}:</strong></span>`];
    matches.slice(0, 15).forEach(schedule => {
      const scheduleName = this.getScheduleDisplayName(schedule);
      const scheduleLink = schedule.type?.id
        ? this.inlineRouteLink(scheduleName, `/schedulesV2/${schedule.type.id}`)
        : this.toHtml(scheduleName);
      const status = ScheduleStatusTimeEnum[schedule.statusTime] ?? 'Open';
      const dates = this.getScheduleDateDetails(schedule);
      const dateText = dates.length ? ` — <span class="grey">${dates.join(' · ')}</span>` : '';
      lines.push(`• <strong>${scheduleLink}</strong> — <strong>${this.toHtml(status.replace(/([a-z])([A-Z])/g, '$1 $2'))}</strong>${dateText}`);
    });
    if (matches.length > 15) lines.push(`<span class="grey"><em>...and ${matches.length - 15} more</em></span>`);

    return { content: lines.join('<br>') };
  }

  /**
   * Matches a full schedule type name or acronym without depending on hard-coded schedule names.
   * @param schedule Schedule catalog row.
   * @param query Optional schedule type wording from the question.
   */
  private scheduleMatchesType(schedule: Schedule, query?: string): boolean {
    const normalizedQuery = this.normalizeTypeText(query);
    if (!normalizedQuery) return true;
    const name = this.normalizeTypeText(schedule.type?.name);
    const acronym = this.normalizeTypeText(schedule.type?.acronym);
    return acronym === normalizedQuery || name.includes(normalizedQuery);
  }

  /**
   * Builds the user-facing schedule type name and appends its acronym when useful.
   * @param schedule Schedule catalog row.
   */
  private getScheduleDisplayName(schedule: Schedule): string {
    const name = schedule.type?.name ?? schedule.name ?? 'Schedule';
    const acronym = schedule.type?.acronym;
    return acronym && this.normalizeTypeText(acronym) !== this.normalizeTypeText(name) ? `${name} (${acronym})` : name;
  }

  /**
   * Returns unique assigned resource names, including custom text resources.
   * @param schedule Schedule catalog row.
   */
  private getScheduleResourceNames(schedule: Schedule): string[] {
    return (schedule.scheduleResources ?? [])
      .map(item => item.resource?.name ?? item.resourceString)
      .filter((name): name is string => !!name)
      .filter((name, index, all) => all.indexOf(name) === index);
  }

  /**
   * Uses an approved extension date as the expiration boundary when present.
   * @param schedule Schedule catalog row.
   */
  private getScheduleExpirationDate(schedule: Schedule): Date | null {
    const value = schedule.isExtended && schedule.extendedDate ? schedule.extendedDate : schedule.expireDate;
    return value ? new Date(value) : null;
  }

  /**
   * Returns a sortable expiration timestamp, placing missing dates last.
   * @param schedule Schedule catalog row.
   */
  private getScheduleExpirationTime(schedule: Schedule): number {
    return this.getScheduleExpirationDate(schedule)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  }

  /**
   * Returns the explicit review date or next due date as a sortable timestamp.
   * @param schedule Schedule catalog row.
   */
  private getScheduleReviewTime(schedule: Schedule): number {
    const date = schedule.scheduleFor ?? schedule.nextDue;
    return date ? new Date(date).getTime() : Number.MAX_SAFE_INTEGER;
  }

  /**
   * Formats the schedule dates shown in the ASCC schedule table.
   * @param schedule Schedule catalog row.
   */
  private getScheduleDateDetails(schedule: Schedule): string[] {
    const dates: string[] = [];
    if (schedule.lastCompleted) dates.push(`Last completed ${this.fmtDate(schedule.lastCompleted)}`);
    if (schedule.nextDue) dates.push(`Next due ${this.fmtDate(schedule.nextDue)}`);
    if (schedule.scheduleFor) dates.push(`Scheduled for ${this.fmtDate(schedule.scheduleFor)}`);
    if (schedule.isExtended && schedule.extendedDate) dates.push(`Extended to ${this.fmtDate(schedule.extendedDate)}`);
    return dates;
  }

  /**
   * Returns the most relevant schedule date for chronological ordering.
   * @param schedule Schedule catalog row.
   */
  private getScheduleDateSortTime(schedule: Schedule): number {
    const date = schedule.isExtended && schedule.extendedDate
      ? schedule.extendedDate
      : schedule.scheduleFor ?? schedule.nextDue;
    return date ? new Date(date).getTime() : Number.MAX_SAFE_INTEGER;
  }

  // -- Review Requests -----------------------------------------

  /**
   * Loads the review-cycle view requested for the selected BRC or ARC board.
   * @param intent Parsed board and pending/in-progress scope.
   */
  private handleReviewRequests(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    const reviewCycles = this.store.selectSnapshot(ReviewCyclesState.read) ?? [];
    if (intent.reviewScope === 'inProgress') {
      const reviews = this.store.selectSnapshot(ReviewsState.read) ?? [];
      // Active cycles and review names are stored separately, so load either catalog only when it is absent.
      return forkJoin({
        cycles: reviewCycles.length ? of(reviewCycles) : this.reviewsService.getReviewCycles(),
        reviews: reviews.length ? of(reviews) : this.reviewsService.getReviews(),
      }).pipe(
        map(result => this.buildInProgressReviewsResponse(result.cycles, result.reviews, intent)),
        catchError(() => of({ content: '<span class="red">⚠</span> Could not load in-progress reviews right now.', isError: true })),
      );
    }

    if (reviewCycles.length) return of(this.buildPendingReviewRequestsResponse(reviewCycles, intent));

    return this.reviewsService.getReviewCycles().pipe(
      map(cycles => this.buildPendingReviewRequestsResponse(cycles, intent)),
      catchError(() => of({ content: '<span class="red">⚠</span> Could not load pending review requests right now.', isError: true })),
    );
  }

  /**
   * Lists reviews belonging to active cycles for the selected review committee.
   * @param reviewCycles Review cycles available from ASCC.
   * @param reviews Reviews used to resolve the display name for each active cycle.
   * @param intent Parsed BRC or ARC request.
   */
  private buildInProgressReviewsResponse(reviewCycles: ReviewCycle[], reviews: Review[], intent: ParsedIntent): Partial<ChatMessage> {
    const board = intent.reviewBoard ?? 'BRC';
    const activeCycleIds = new Set(reviewCycles
      .filter(cycle => cycle.status === ReviewCycleStatus.Approved)
      .filter(cycle => this.reviewCycleBelongsToBoard(cycle, board))
      .map(cycle => cycle.id));
    const cycleById = new Map(reviewCycles.filter(cycle => activeCycleIds.has(cycle.id)).map(cycle => [cycle.id, cycle]));
    const inProgress = reviews
      .filter(review => activeCycleIds.has(review.reviewCycleID))
      .sort((left, right) => new Date(right.reviewDate ?? 0).getTime() - new Date(left.reviewDate ?? 0).getTime());

    if (!inProgress.length) {
      return { content: `<span class="grey">No ${board} reviews are in progress.</span>` };
    }

    const boardName = board === 'BRC' ? 'Beamline Review Committee' : 'Accelerator Review Committee';
    const lines = [`<span class="blue"><strong>${inProgress.length} ${board} (${boardName}) review(s) in progress:</strong></span>`];
    inProgress.slice(0, 15).forEach(review => {
      const cycle = cycleById.get(review.reviewCycleID) ?? review.reviewCycle;
      const serialNo = cycle?.serialNo ?? 'Unknown cycle';
      const reviewName = review.name ?? review.reviewType?.name ?? 'Unnamed review';
      const resource = cycle?.reviewBoard?.resource?.name ?? cycle?.resource?.name ?? cycle?.location ?? 'No resource specified';
      lines.push(`• <strong>${this.inlineRouteLink(serialNo, `/redirect/${serialNo}`)}</strong> — ${this.toHtml(reviewName)} — <span class="grey">${this.toHtml(resource)}</span>`);
    });
    if (inProgress.length > 15) lines.push(`<span class="grey"><em>...and ${inProgress.length - 15} more</em></span>`);

    return { content: lines.join('<br>') };
  }

  /**
   * Matches an active cycle to its configured review board, with resource type as a legacy fallback.
   * @param reviewCycle Active review cycle.
   * @param board Board requested by the user.
   */
  private reviewCycleBelongsToBoard(reviewCycle: ReviewCycle, board: 'BRC' | 'ARC'): boolean {
    const boardType = `${reviewCycle.reviewBoard?.reviewBoardType?.code ?? ''} ${reviewCycle.reviewBoard?.reviewBoardType?.name ?? ''}`.toUpperCase();
    if (/\bBRC\b|BEAMLINE REVIEW COMMITTEE/.test(boardType)) return board === 'BRC';
    if (/\bARC\b|ACCELERATOR REVIEW COMMITTEE/.test(boardType)) return board === 'ARC';

    const resourceType = reviewCycle.reviewBoard?.resource?.type ?? reviewCycle.resource?.type;
    return board === 'ARC'
      ? resourceType === ResourceType.Accelerator
      : resourceType === ResourceType.Beamline || resourceType === ResourceType.Branchline;
  }

  /**
   * Applies the Reviews module's pending status and notification target rules before formatting results.
   * @param reviewCycles Review requests available from ASCC.
   * @param intent Parsed BRC or ARC request.
   */
  private buildPendingReviewRequestsResponse(reviewCycles: ReviewCycle[], intent: ParsedIntent): Partial<ChatMessage> {
    const board = intent.reviewBoard ?? 'BRC';
    const pending = reviewCycles
      .filter(cycle => cycle.status === ReviewCycleStatus.Pending)
      .filter(cycle => this.reviewRequestTargetsBoard(cycle, board))
      .sort((left, right) => new Date(left.createdOn ?? 0).getTime() - new Date(right.createdOn ?? 0).getTime());

    if (!pending.length) {
      return { content: `<span class="grey">No pending ${board} review requests were found.</span>` };
    }

    const boardName = board === 'BRC' ? 'Beamline Review Committee' : 'Accelerator Review Committee';
    const lines = [`<span class="blue"><strong>${pending.length} pending ${board} (${boardName}) review request(s):</strong></span>`];
    pending.slice(0, 15).forEach(cycle => {
      const location = cycle.resource?.name ?? cycle.location ?? 'No location specified';
      const proposed = this.formatReviewRequestTimeline(cycle.proposedTimeline);
      const requestedBy = cycle.createdByUser?.name ? ` — Requested by ${this.toHtml(cycle.createdByUser.name)}` : '';
      lines.push(`• <strong>${this.inlineRouteLink(cycle.serialNo, '/review-requests')}</strong> — ${this.toHtml(location)} — <span class="grey">Proposed ${proposed}</span>${requestedBy}`);
    });
    if (pending.length > 15) lines.push(`<span class="grey"><em>...and ${pending.length - 15} more</em></span>`);

    return { content: lines.join('<br>') };
  }

  /**
   * Resolves explicit ARC/BRC targets and falls back to the resource category for older requests.
   * @param reviewCycle Pending review request.
   * @param board Board requested by the user.
   */
  private reviewRequestTargetsBoard(reviewCycle: ReviewCycle, board: 'BRC' | 'ARC'): boolean {
    if (reviewCycle.notifyARC != null || reviewCycle.notifyBRC != null) {
      return board === 'BRC' ? reviewCycle.notifyBRC === true : reviewCycle.notifyARC === true;
    }

    const resourceType = reviewCycle.resource?.type;
    return board === 'ARC'
      ? resourceType === ResourceType.Accelerator
      : resourceType === ResourceType.Beamline || resourceType === ResourceType.Branchline;
  }

  /**
   * Preserves free-text proposed timelines while formatting actual dates consistently.
   * @param value Proposed review timeline stored on the request.
   */
  private formatReviewRequestTimeline(value: string): string {
    const date = new Date(value);
    return !Number.isNaN(date.getTime()) ? this.fmtDate(date) : this.toHtml(value || 'Not specified');
  }

  /**
   * Removes the time portion for stable local calendar comparisons.
   * @param date Date to normalize.
   */
  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  /**
   * Adds calendar days without assuming every local day is exactly 24 hours.
   * @param date Starting date.
   * @param days Calendar days to add.
   */
  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  // -- Navigate -------------------------------------------------

  private handleNavigate(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    const sn = intent.serialNo!;
    return of({
      content: `Click below to open <strong>${this.toHtml(sn)}</strong>:`,
      links: [{ label: `Open ${sn}`, route: `/redirect/${sn}` }],
    });
  }

  // -- Permission Info ------------------------------------------

  private handlePermissionInfo(intent: ParsedIntent): Partial<ChatMessage> {
    const formType = intent.formType?.toUpperCase();
    const createPrivileges = this.getCreatePrivilegeRequirements();

    if (intent.action === 'create' && formType && createPrivileges[formType]) {
      const requirement = createPrivileges[formType];
      return this.formatPrivilegeRequirement(requirement.actionLabel, requirement.privilege, requirement.fallbackName, requirement.route);
    }

    if (intent.action === 'create' && intent.isChecklist) {
      return this.genericChecklistPermissionResponse();
    }

    return {
      content: `I do not have a permission rule for <strong>${this.toHtml(formType ?? 'that form')}</strong> yet.`,
    };
  }

  private genericChecklistPermissionResponse(): Partial<ChatMessage> {
    return {
      content: [
        'Generic checklist creation is controlled by <span class="blue"><strong>Create Checklist</strong></span> role permissions on the checklist document/template type.',
        'There is not one global role for every checklist; the allowed roles depend on the checklist type you are creating.',
        '<span class="grey">For Key Enable checklists, ask:</span> <strong>"What role do I need to create a KEnable?"</strong>',
      ].join('<br>'),
    };
  }

  private formatPrivilegeRequirement(actionLabel: string, privilege: PrivilegeEnum, privilegeName: string, route: string): Partial<ChatMessage> {
    const rolePrivileges = this.store.selectSnapshot(RolePrivilegeState.read) ?? [];
    const privilegeDisplayName = rolePrivileges.find(rp => rp.privilege === privilege)?.privilegeObj?.name ?? privilegeName;
    const roleNames = rolePrivileges
      .filter(rp => rp.privilege === privilege)
      .map(rp => rp.role?.code || rp.role?.name)
      .filter((name): name is string => !!name)
      .filter((name, index, all) => all.indexOf(name) === index)
      .sort((a, b) => a.localeCompare(b));

    const hasAccess = this.hasPrivilege(privilege);
    const lines = [
      `To <strong>${this.toHtml(actionLabel)}</strong>, you need the <span class="blue"><strong>${this.toHtml(privilegeDisplayName)}</strong></span> privilege.`,
      hasAccess
        ? '<span class="green">✓</span> Your current account has this privilege.'
        : '<span class="yellow">▶</span> Your current account does not appear to have this privilege.',
    ];

    if (roleNames.length) {
      lines.push(`<span class="grey">Roles currently granting it:</span> ${roleNames.map(role => `<strong>${this.toHtml(role)}</strong>`).join(', ')}`);
    } else {
      lines.push('<span class="grey">I could not find loaded role mappings, so ask an admin for the role that grants this privilege.</span>');
    }

    return {
      content: lines.join('<br>'),
      links: [{ label: privilegeName.replace(/^Create /, 'Go to '), route }],
    };
  }

  // -- Help -----------------------------------------------------

  private helpResponse(): Partial<ChatMessage> {
    return {
      content: [
        'I am the <strong>ASCC Workflow Assistant</strong>. Here is what I can do:',
        '',
        '<span class="blue"><strong>Form Status</strong></span>',
        '• "What is the status of SCF-24-001?"',
        '• "Where is RSSWA-24-001?"',
        '',
        '<span class="green"><strong>Catalog/Template Approvals</strong></span>',
        '• "What catalog needs my approval?"',
        '• "What checklist templates need approval?"',
        '• "What needs my approval?"',
        '',
        '<span class="green"><strong>Workflow Signatures</strong></span>',
        '• "What forms or checklists are waiting to be signed for me?"',
        '• "Is SCF-26-023 waiting for me?"',
        '• "What forms need my approval?"',
        '• "What checklists need my approval?"',
        '• "What needs my signature?"',
        '• "What can I do in ASCC?"',
        '',
        '<span class="orange"><strong>Procedure Training</strong></span>',
        '• "What are my missing trainings?"',
        '• "How many missing trainings do I have?"',
        '• "What trainings are not approved yet?"',
        '• "How many trainings are not approved?"',
        '• "What trainings do I have to approve?"',
        '• "How many trainings do I have to approve?"',
        '',
        '<span class="yellow"><strong>Blocking Info</strong></span>',
        '• "Why is RSSWA-24-001 stuck?"',
        '• "Who needs to sign SCF-24-001?"',
        '',
        '<span class="cyan"><strong>List Forms & Checklists</strong></span>',
        '• "What SCF forms are open?"',
        '• "Which Start up Checklists are open?"',
        '• "What are the most recently closed RSSWA forms?"',
        '• "Show active SCFs"',
        '• "List open RSSWAs"',
        '• "Show KE forms"',
        '• "List all active forms"',
        '• "Show active checklists"',
        '',
        '<span class="blue"><strong>Permissions</strong></span>',
        '• "What role do I need to create a SCF?"',
        '• "What role do I need to create a RSSWA?"',
        '• "What role do I need to create a PPSTB?"',
        '• "What role do I need to create an EB?"',
        '• "What role do I need to create an RSF?"',
        '• "What role do I need to create an ABSI?"',
        '• "What role do I need to create a KEnable?"',
        '• "What permission is required to create SCF?"',
        '',
        '<span class="orange"><strong>Resource Status</strong></span>',
        '• "What is the status of BL 5.0.2?"',
        '• "Show beamline 8.3.1"',
        '• "Status of branchline 1.4"',
        '• "Accelerator status"',
        '',
        '<span class="cyan"><strong>Affected Systems</strong></span>',
        '• "What forms are affecting BL 3.2.1 now?"',
        '• "What open SCFs affect beamline 5.0.2?"',
        '• "Which beamlines are affected by RSFs now?"',
        '• "What beamlines are impacted by RSSWAs today?"',
        '',
        '<span class="yellow"><strong>Schedules</strong></span>',
        '• "Which schedules are expired now?"',
        '• "Which Radiation Survey Schedules are expired now?"',
        '• "Which RSS schedules are expired now?"',
        '• "Which schedules are going to expire next week?"',
        '• "When is BL 3.2.1 scheduled for a checklist review?"',
        '• "List schedule dates for BL 5.0"',
        '',
        '<span class="yellow"><strong>Review Requests</strong></span>',
        '• "List pending BRC requests"',
        '• "Which ARC review requests are pending?"',
        '• "List BRC reviews in progress"',
        '• "Which ARC reviews are active?"',
        '• "Show pending Beamline Review Committee requests"',
        '• "What Accelerator Review Committee requests are waiting?"',
        '',
        '<span class="cyan"><strong>Search & Print</strong></span>',
        '• "Search SCF-26-035"',
        '• "Search shielding work"',
        '• "Print this page"',
        '',
        '<span class="orange"><strong>Navigate</strong></span>',
        '• "Take me to SCF-24-001"',
        '• "Open RSSWA-24-001"',
      ].join('<br>'),
    };
  }

  private unknownResponse(_text: string): Partial<ChatMessage> {
    return {
      content: `I did not understand that. Try asking about a specific form or type <strong>help</strong> to see what I can do.`,
    };
  }

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------

  private statusDot(status?: number): { dot: string; cssClass: string } {
    switch (status) {
      case FormStatusEnum.Active: return { dot: '●', cssClass: 'green' };
      case FormStatusEnum.Draft: return { dot: '●', cssClass: 'grey' };
      case FormStatusEnum.WorkApprovals: return { dot: '●', cssClass: 'yellow' };
      case FormStatusEnum.Closed: return { dot: '●', cssClass: 'grey' };
      case FormStatusEnum.Canceled: return { dot: '●', cssClass: 'red' };
      case FormStatusEnum.WorkCompleted: return { dot: '●', cssClass: 'green' };
      case FormStatusEnum.Amendment: return { dot: '●', cssClass: 'orange' };
      default: return { dot: '●', cssClass: 'blue' };
    }
  }

  private handleCapabilitySummary(): Partial<ChatMessage> {
    const roleIds = this.currentUser?.userRole?.map(ur => ur.roleID).filter((id): id is number => id != null) ?? [];
    const roles = this.currentUser?.userRole
      ?.map(ur => ur.role?.code || ur.role?.name || (ur.roleID ? `Role ${ur.roleID}` : null))
      .filter((role): role is string => !!role)
      .filter((role, index, all) => all.indexOf(role) === index)
      .sort((a, b) => a.localeCompare(b)) ?? [];

    const rolePrivileges = this.store.selectSnapshot(RolePrivilegeState.read) ?? [];
    const privileges = this.store.selectSnapshot(PrivilegeState.read) ?? [];
    const grantedPrivilegeIds = rolePrivileges
      .filter(rp => roleIds.includes(rp.roleID))
      .map(rp => rp.privilege)
      .filter((privilege, index, all) => all.indexOf(privilege) === index);

    const privilegeName = (privilege: PrivilegeEnum) =>
      privileges.find(p => p.privilegeEnum === privilege)?.name ??
      rolePrivileges.find(rp => rp.privilege === privilege)?.privilegeObj?.name ??
      PrivilegeEnum[privilege] ??
      `Privilege ${privilege}`;

    const createHighlights = Object.values(this.getCreatePrivilegeRequirements())
      .filter(item => this.hasPrivilege(item.privilege))
      .map(item => item.actionLabel.replace(/^create /, 'Create '))
      .slice(0, 8);

    const approvalHighlights = [
      { label: 'Submit SCFs', privilege: PrivilegeEnum.SCFSubmit },
      { label: 'Approve SCF operational changes', privilege: PrivilegeEnum.SCFOperationalApproval },
      { label: 'Approve SCF RADS changes', privilege: PrivilegeEnum.SCFApproveRADSChanges },
      { label: 'Submit RSSWAs', privilege: PrivilegeEnum.RSSWA },
      { label: 'Submit RSFs', privilege: PrivilegeEnum.RSFSubmit },
      { label: 'Close ABSI checklists', privilege: PrivilegeEnum.CloseABSI },
    ].filter(item => this.hasPrivilege(item.privilege)).map(item => item.label);

    const shownPrivileges = grantedPrivilegeIds
      .map(privilegeName)
      .filter((name, index, all) => all.indexOf(name) === index)
      .sort((a, b) => a.localeCompare(b))
      .slice(0, 10);

    const lines = [
      '<span class="blue"><strong>Here is what ASCC thinks you can do:</strong></span>',
      roles.length
        ? `<span class="grey">Roles:</span> ${roles.map(role => `<strong>${this.toHtml(role)}</strong>`).join(', ')}`
        : '<span class="grey">Roles:</span> No roles found for your current account.',
    ];

    if (createHighlights.length) {
      lines.push(`<span class="grey">Create access:</span> ${createHighlights.map(item => `<strong>${this.toHtml(item)}</strong>`).join(', ')}`);
    }

    if (approvalHighlights.length) {
      lines.push(`<span class="grey">Workflow access:</span> ${approvalHighlights.map(item => `<strong>${this.toHtml(item)}</strong>`).join(', ')}`);
    }

    if (shownPrivileges.length) {
      const more = grantedPrivilegeIds.length > shownPrivileges.length ? ` <span class="grey"><em>...and ${grantedPrivilegeIds.length - shownPrivileges.length} more</em></span>` : '';
      lines.push(`<span class="grey">Privileges:</span> ${shownPrivileges.map(name => this.toHtml(name)).join(', ')}${more}`);
    } else {
      lines.push('<span class="grey">Privileges:</span> I could not find loaded privilege mappings for your roles.');
    }

    return { content: lines.join('<br>') };
  }

  private getCreatePrivilegeRequirements(): Record<string, { actionLabel: string; privilege: PrivilegeEnum; fallbackName: string; route: string }> {
    return {
      SCF: { actionLabel: 'create a SCF', privilege: PrivilegeEnum.SCFNewForm, fallbackName: 'Create SCF', route: '/scf' },
      RSSWA: { actionLabel: 'create a RSSWA', privilege: PrivilegeEnum.RSSWA, fallbackName: 'Create RSSWA', route: '/rsswa' },
      PPSTB: { actionLabel: 'create a PPSTB', privilege: PrivilegeEnum.PPSTBNewForm, fallbackName: 'Create PPSTB', route: '/ppstb' },
      EB: { actionLabel: 'create an Equipment Bypass', privilege: PrivilegeEnum.EBNewForm, fallbackName: 'Create Equipment Bypass', route: '/eb' },
      EBF: { actionLabel: 'create an Equipment Bypass', privilege: PrivilegeEnum.EBNewForm, fallbackName: 'Create Equipment Bypass', route: '/eb' },
      RSF: { actionLabel: 'create a Radiation Survey Form', privilege: PrivilegeEnum.RSFCreate, fallbackName: 'Create RSF', route: '/rsf' },
      ABSI: { actionLabel: 'create an ABSI checklist', privilege: PrivilegeEnum.CreateABSI, fallbackName: 'Create ABSI', route: '/absi' },
      KE: { actionLabel: 'create a Key Enable checklist', privilege: PrivilegeEnum.CreateKEChecklist, fallbackName: 'Create Key Enable Checklist', route: '/kenable' },
    };
  }

  private hasKeyword(text: string, keyword: string): boolean {
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const boundary = /^[a-z0-9]+$/i.test(keyword)
      ? new RegExp(`\\b${escaped}s?\\b`, 'i')
      : new RegExp(escaped, 'i');
    return boundary.test(text);
  }

  /**
   * Normalizes common typos and phrasing variants before intent parsing.
   * @param text Raw user question.
   */
  private normalizeQuestionText(text: string): string {
    let normalized = text.toLowerCase().trim();
    const replacements: Array<[RegExp, string]> = [
      [/\bwating\b/g, 'waiting'],
      [/\bwainting\b/g, 'waiting'],
      [/\baprove\b/g, 'approve'],
      [/\baproval\b/g, 'approval'],
      [/\bapprovel\b/g, 'approval'],
      [/\bappoval\b/g, 'approval'],
      [/\btrainigns\b/g, 'trainings'],
      [/\btranings\b/g, 'trainings'],
      [/\btraings\b/g, 'trainings'],
      [/\bmissng\b/g, 'missing'],
      [/\bwich\b/g, 'which'],
      [/\bwhare\b/g, 'what'],
      [/\bwat\b/g, 'what'],
      [/\bfrm\b/g, 'form'],
      [/\bfrms\b/g, 'forms'],
      [/\bcheklist\b/g, 'checklist'],
      [/\bcheklists\b/g, 'checklists'],
      [/\bchklst\b/g, 'checklist'],
      [/\bafect\b/g, 'affect'],
      [/\bafects\b/g, 'affects'],
      [/\bafected\b/g, 'affected'],
      [/\bafecting\b/g, 'affecting'],
      [/\bbeamlinee\b/g, 'beamline'],
      [/\bbeamine\b/g, 'beamline'],
      [/\bbeamines\b/g, 'beamlines'],
      [/\bscheduel\b/g, 'schedule'],
      [/\bscheduels\b/g, 'schedules'],
      [/\bschdule\b/g, 'schedule'],
      [/\bschdules\b/g, 'schedules'],
      [/\bscedule\b/g, 'schedule'],
      [/\bscedules\b/g, 'schedules'],
      [/\bexpierd\b/g, 'expired'],
      [/\bexpried\b/g, 'expired'],
      [/\bcommitee\b/g, 'committee'],
      [/\bprogess\b/g, 'progress'],
      [/\bprogres\b/g, 'progress'],
      [/\bstart\s*up\b/g, 'startup'],
      [/\bkey\s*enable\b/g, 'kenable'],
      [/\bk\s*enable\b/g, 'kenable'],
    ];

    replacements.forEach(([pattern, value]) => normalized = normalized.replace(pattern, value));
    return normalized.replace(/\s+/g, ' ');
  }

  /**
   * Extracts a natural document type phrase from list questions, such as "magnet testing".
   * @param text Lowercase user question.
   */
  private extractDocumentTypeQuery(text: string): string | undefined {
    const match = text.match(/\b(?:what|which|show|list|find|get|all|open|active|closed|draft|recent|recently|latest|newest|most|are|is|the|forms?)\s+(.+?)\s+(?:checklist|checklists|forms?)\b/);
    const query = match?.[1]
      ?.replace(/\b(open|active|closed|draft|canceled|cancelled|completed|recent|recently|latest|newest|most|are|is|the|all)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return query && !this.formTypeStringToEnum(query.toUpperCase()) ? query : undefined;
  }

  /**
   * Extracts the schedule type phrase before "schedule", preserving full names and acronyms.
   * @param text Normalized user question.
   */
  private extractScheduleTypeQuery(text: string): string | undefined {
    const scheduleIndex = text.search(/\bschedules?\b/);
    if (scheduleIndex < 0) return undefined;

    const query = text.slice(0, scheduleIndex)
      .replace(/\b(which|what|show|list|find|all|the|of)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return query || undefined;
  }

  private resolveSignatureScope(text: string): ParsedIntent['signatureScope'] {
    if (/\bchecklists?\b/.test(text)) return 'checklists';
    if (/\bforms?\b/.test(text)) return 'forms';
    return 'all';
  }

  private isSerialWaitingForMeQuestion(text: string): boolean {
    return /\b(waiting|wait|pending|queued)\b.*\b(for me|on me|on my queue|my queue|my signature|my approval)\b/.test(text) ||
      /\b(do i|should i|am i supposed to)\b.*\b(sign|approve)\b/.test(text) ||
      /\b(need|needs)\b.*\b(me|my signature|my approval)\b/.test(text);
  }

  private sameSerial(left: string | undefined, right: string | undefined): boolean {
    return this.normalizeSerial(left) === this.normalizeSerial(right);
  }

  private normalizeSerial(serial: string | undefined): string {
    return (serial ?? '').trim().toUpperCase().replace(/\s+/g, '-');
  }

  private toHtml(value: unknown): string {
    return String(value ?? '').replace(/[&<>"']/g, ch => {
      const replacements: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      };
      return replacements[ch];
    });
  }

  private inlineRouteLink(label: string, route: string): string {
    const safeRoute = this.toHtml(route);
    return `<a class="assistant-inline-link" href="${safeRoute}" data-route="${safeRoute}">${this.toHtml(label)}</a>`;
  }

  private fmtDate(d: Date | string | undefined): string {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /**
   * Resolves a natural-language status filter into one or more ASCC status ids.
   * @param name Parsed status filter.
   */
  private statusNameToIds(name: string): FormStatusEnum[] | null {
    const map: Record<string, FormStatusEnum[]> = {
      Open: [FormStatusEnum.Draft, FormStatusEnum.Closed, FormStatusEnum.Canceled],
      Active: [FormStatusEnum.Active],
      Draft: [FormStatusEnum.Draft],
      WorkApprovals: [FormStatusEnum.WorkApprovals],
      PendingActivation: [FormStatusEnum.PendingActivation],
      AwaitingInspection: [FormStatusEnum.AwaitingInspection],
      HoldPoint: [FormStatusEnum.HoldPoint],
      Closed: [FormStatusEnum.Closed],
      Canceled: [FormStatusEnum.Canceled],
      WorkCompleted: [FormStatusEnum.WorkCompleted],
      Amendment: [FormStatusEnum.Amendment],
      PendingReview: [FormStatusEnum.PendingReview],
      Reviewing: [FormStatusEnum.Reviewing],
      Correcting: [FormStatusEnum.Correcting],
      ApprovalPending: [FormStatusEnum.ApprovalPending],
      Approvals: [FormStatusEnum.Approvals],
      Preparation: [FormStatusEnum.Preparation],
      CloseOut: [FormStatusEnum.CloseOut],
      ReviewAndConcurrence: [FormStatusEnum.ReviewAndConcurrence],
      Testing: [FormStatusEnum.Testing],
      ShieldingInspections: [FormStatusEnum.ShieldingInspections],
      PpsTesting: [FormStatusEnum.PpsTesting],
      PpsTestingComplete: [FormStatusEnum.PpsTestingComplete],
      PendingRequesterConcurrence: [FormStatusEnum.PendingRequesterConcurrence],
      PendingSafetyControls: [FormStatusEnum.PendingSafetyControls],
    };
    return map[name] ?? null;
  }

  /**
   * Applies status matching, with Open treated as every non-draft, non-closed, and non-canceled document.
   * @param document Document catalog item to test.
   * @param statusIds Status ids resolved from the user question.
   * @param statusFilter Original parsed status filter.
   */
  private documentMatchesStatus(document: Document, statusIds: FormStatusEnum[], statusFilter?: string): boolean {
    if (statusFilter === 'Open') return !statusIds.includes(document.status as FormStatusEnum);
    return statusIds.includes(document.status as FormStatusEnum);
  }

  /**
   * Matches both form enum ids and document type names/codes for checklist-specific wording.
   * @param document Document catalog item to test.
   * @param typeFilter Parsed form type enum.
   * @param formType User-facing form/checklist type.
   */
  private documentMatchesType(document: Document, typeFilter: number, formType?: string): boolean {
    if (document.type === typeFilter) return true;

    const normalizedFormType = this.normalizeTypeText(formType);
    const documentTypeText = [
      document.documentType?.code,
      document.documentType?.name,
      document.documentType?.description,
    ].map(value => this.normalizeTypeText(value)).filter(Boolean).join(' ');

    return !!normalizedFormType && documentTypeText.includes(normalizedFormType);
  }

  /**
   * Matches free-text checklist/form type phrases against document type metadata.
   * @param document Document catalog item to test.
   * @param query User-provided type phrase.
   */
  private documentMatchesTypeQuery(document: Document, query: string): boolean {
    const normalizedQuery = this.normalizeTypeText(query);
    const documentTypeText = [
      document.documentType?.code,
      document.documentType?.name,
      document.documentType?.description,
    ].map(value => this.normalizeTypeText(value)).filter(Boolean).join(' ');

    return !!normalizedQuery && documentTypeText.includes(normalizedQuery);
  }

  /**
   * Sorts document list results by recent activity when requested, then serial number.
   * @param left First document.
   * @param right Second document.
   * @param sort Parsed list sort option.
   */
  private sortDocuments(left: Document, right: Document, sort?: ParsedIntent['listSort']): number {
    if (sort === 'recent') {
      return this.getDocumentSortTime(right) - this.getDocumentSortTime(left);
    }
    return (right.serialNo ?? '').localeCompare(left.serialNo ?? '');
  }

  /**
   * Chooses the best available date for recent document lists.
   * @param document Document catalog item.
   */
  private getDocumentSortDate(document: Document): Date | string | undefined {
    return document.updatedOn ?? document.createdOn;
  }

  private getDocumentSortTime(document: Document): number {
    const date = this.getDocumentSortDate(document);
    return date ? new Date(date).getTime() || 0 : 0;
  }

  private getStatusDisplayName(status: string): string {
    const map: Record<string, string> = {
      WorkApprovals: 'Work Approvals',
      WorkCompleted: 'Work Completed',
      PendingActivation: 'Pending Activation',
      AwaitingInspection: 'Awaiting Inspection',
      HoldPoint: 'Hold Point',
      PendingReview: 'Pending Review',
      ApprovalPending: 'Approval Pending',
      CloseOut: 'Close Out',
      ReviewAndConcurrence: 'Review and Concurrence',
      ShieldingInspections: 'Shielding Inspections',
      PpsTesting: 'PPS Testing',
      PpsTestingComplete: 'PPS Testing Complete',
      PendingRequesterConcurrence: 'Pending Requester Concurrence',
      PendingSafetyControls: 'Pending Safety Controls',
    };
    return map[status] ?? status;
  }

  private normalizeTypeText(value?: string | null): string {
    return (value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private formTypeStringToEnum(ft: string): number | null {
    const map: Record<string, number> = {
      SCF: FormType.SCF,
      KE: FormType.KE,
      RSSWA: FormType.RSSWA,
      PPSTB: FormType.PPSTB,
      RSF: FormType.RSF,
      EB: FormType.EBF,
      EBF: FormType.EBF,
      ABSI: FormType.ABSI,
    };
    return map[ft.toUpperCase()] ?? null;
  }

  private routeForFormTypeEnum(formType: number | null | undefined): string {
    const routes: Record<number, string> = {
      [FormType.SCF]: '/scf',
      [FormType.KE]: '/kenable',
      [FormType.RSSWA]: '/rsswa',
      [FormType.PPSTB]: '/ppstb',
      [FormType.RSF]: '/rsf',
      [FormType.EBF]: '/eb',
      [FormType.ABSI]: '/absi',
    };
    return (formType != null ? routes[formType] : null) ?? '/home';
  }

  // ------------------------------------------------------------
  // Resource Status (beamline / branchline / accelerator)
  // ------------------------------------------------------------

  private extractResourceName(text: string): string | undefined {
    const m = text.match(/(?:status\s+of\s+|status\s+for\s+|of\s+|for\s+)?([A-Z0-9][\w\s./-]*)/i);
    return m ? m[1].trim() : undefined;
  }

  private handleResourceStatus(intent: ParsedIntent): Observable<Partial<ChatMessage>> {
    const kind = intent.resourceKind;
    const query = (intent.resourceName ?? '').trim().toLowerCase();

    if (!kind || !query) {
      return of({ content: 'Please specify a beamline, branchline, or accelerator name.', type: 'bot' });
    }

    if (kind === 'beamline') {
      const all: BeamlineStatus[] = this.store.selectSnapshot(BeamlineStatusState.read) ?? [];
      const match = all.find(b =>
        b.beamlineName?.toLowerCase().replace(/\s+/g, '') === query.replace(/\s+/g, '') ||
        b.beamlineName?.toLowerCase().includes(query)
      );
      if (!match) {
        const names = all.slice(0, 8).map(b => `<span class="blue">${this.toHtml(b.beamlineName)}</span>`).join(', ');
        return of({ content: `No beamline found matching <b>${this.toHtml(intent.resourceName)}</b>. Known beamlines include: ${names}…`, type: 'bot' });
      }
      return of({ content: this.formatBeamlineStatus(match), type: 'bot' });
    }

    if (kind === 'branchline') {
      const all: IResource[] = this.store.selectSnapshot(state => state['Branchlines']?.data) ?? [];
      const match = all.find(r =>
        r.name?.toLowerCase().replace(/\s+/g, '') === query.replace(/\s+/g, '') ||
        r.name?.toLowerCase().includes(query)
      );
      if (!match) {
        const names = all.slice(0, 8).map(r => `<span class="blue">${this.toHtml(r.name)}</span>`).join(', ');
        return of({ content: `No branchline found matching <b>${this.toHtml(intent.resourceName)}</b>. Known branchlines include: ${names}…`, type: 'bot' });
      }
      return of({ content: this.formatBranchlineStatus(match), type: 'bot' });
    }

    if (kind === 'accelerator') {
      const all: AcceleratorStatus[] = this.store.selectSnapshot(AcceleratorStatusState.read) ?? [];
      const match = all.find(a =>
        a.name?.toLowerCase().includes(query)
      );
      if (!match) {
        const names = all.slice(0, 8).map(a => `<span class="blue">${this.toHtml(a.name)}</span>`).join(', ');
        return of({ content: `No accelerator found matching <b>${this.toHtml(intent.resourceName)}</b>. Known accelerators include: ${names}…`, type: 'bot' });
      }
      return of({ content: this.formatAcceleratorStatus(match), type: 'bot' });
    }

    return of({ content: 'I could not identify the resource type.', type: 'bot' });
  }

  private formatBeamlineStatus(b: BeamlineStatus): string {
    const rssColor = (b.rssColor ?? 'grey').toLowerCase();
    const pssColor = (b.pssColor ?? 'grey').toLowerCase();
    const rssClass = rssColor === 'green' ? 'green' : rssColor === 'red' ? 'red' : 'yellow';
    const pssClass = pssColor === 'green' ? 'green' : pssColor === 'red' ? 'red' : 'yellow';
    const keLine = b.keReady?.label
      ? `\n<b>KE Ready:</b> <span class="${b.keReady.label === 'Ready' ? 'green' : 'yellow'}">${this.toHtml(b.keReady.label)}</span>`
      : '';
    return `<b>Beamline:</b> <span class="blue">${this.toHtml(b.beamlineName)}</span>\n` +
      `<b>Shutter:</b> ${this.toHtml(b.shutterName ?? 'N/A')}\n` +
      `<b>RSS Status:</b> <span class="${rssClass}">${this.toHtml(b.rssStatus ?? 'Unknown')}</span>\n` +
      `<b>Shutter Status:</b> <span class="${pssClass}">${this.toHtml(b.shutterStatus ?? 'Unknown')}</span>` +
      keLine;
  }

  private formatBranchlineStatus(r: IResource): string {
    const statusText = r.operationalLockOut ? 'Locked Out' : 'Operational';
    const statusClass = r.operationalLockOut ? 'red' : 'green';
    const keLine = r.keReady ? `\n<b>KE Ready:</b> <span class="${this.toHtml(r.keColor ?? 'grey')}">${this.toHtml(r.keReady)}</span>` : '';
    return `<b>Branchline:</b> <span class="blue">${this.toHtml(r.name)}</span>\n` +
      `<b>Status:</b> <span class="${statusClass}">${statusText}</span>` +
      keLine;
  }

  private formatAcceleratorStatus(a: AcceleratorStatus): string {
    const status = (a.status ?? '').toLowerCase();
    const colorClass = /\b(green|available|operational|running|ready|normal|up)\b/.test(status) ? 'green' :
      /\b(red|unavailable|down|offline|fault|alarm|shutdown)\b/.test(status) ? 'red' : 'yellow';
    const extraInfo = a.moreInfo ? `\n<b>Info:</b> ${this.toHtml(a.moreInfo)}` : '';
    return `<b>Accelerator:</b> <span class="blue">${this.toHtml(a.name)}</span>\n` +
      `<b>Status:</b> <span class="${colorClass}">${this.toHtml(a.status ?? 'Unknown')}</span>` +
      extraInfo;
  }
}
